/**
 * Lead Details Component
 * ======================
 * Displays detailed lead information, the due diligence report, and edit
 * functionality.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leadAPI, organizationAPI } from '../../api';
import { Card, CardHeader, CardBody, Button, Badge, ConfirmationModal, Modal } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../hooks/useAuth';
import { useOrg } from '../../hooks';
import { useToast } from '../../contexts/ToastContext';
import LeadEditForm from './LeadEditForm';
import DiscoveryQuestionnaire from './DiscoveryQuestionnaire';
import TelephonicConversationModal from './TelephonicConversationModal';
import transcriptAPI from '../../api/transcripts';
import meetingAPI from '../../api/meetings';
import mockAPI from '../../api/mock';
import { isDevOrStaging } from '../../utils/environment';
import { getDecisionEngineOutput, getExternalResearch, getLeadProposals, activateProposal, proceedFromProposalReview, declineFromProposalReview, scheduleFollowupFromProposalReview, getProposalProcessingStatus, getWorkerOutputs, forceGenerateProposal, regenerateProposal, triggerDecisionEngine } from '../../api/proposals';
import { ProposalStatusBadge, DecisionEngineCard, ExternalResearchCard, ProposalGenerationProgress } from '../proposal';
import LeadStepper from './LeadStepper';
import { parseTranscriptText } from '../../utils/transcripts';
import { isHttpUrl } from '../../utils/validation';
import TranscriptTabView from '../TranscriptTabView';
import PromptVersionsBadge from '../ui/PromptVersionsBadge';
import DocumentsCard from './DocumentsCard';
import DiligenceCard from './DiligenceCard';

const VALID_TABS = ['research', 'meetings', 'questionnaire', 'proposals'];

// A diligence run that produced a report completes the stepper's first step.
// `completed_gate_failed` counts: the report exists and an operator can read
// it, with a warning. The old source, `lead.research_status`, is permanently
// `pending` since 2026-09-07 and is no longer read for this.
const DILIGENCE_COMPLETE_STATUSES = ['completed', 'completed_gate_failed'];

const WORKER_LIST = [
  { type: 'origination', label: 'Origination Intelligence' },
  { type: 'regulatory_structuring', label: 'Regulatory & Structuring' },
  { type: 'technology', label: 'Technology Intelligence' },
  { type: 'distribution', label: 'Distribution Intelligence' },
];

const MEETING_PROMPT_LABELS = {
  manual_transcript_summary: 'Manual Summary',
  transcript_consolidation: 'Consolidation',
  questionnaire_generation: 'Questionnaire Gen',
  questionnaire_update_detection: 'Questionnaire Upd.',
  email_questionnaire_update_detection: 'Notes Upd.',
};
const MEETING_PROMPT_ORDER = Object.keys(MEETING_PROMPT_LABELS);

const EMAIL_PROMPT_LABELS = {
  email_questionnaire_update_detection: 'Update Detection',
};
const EMAIL_PROMPT_ORDER = ['email_questionnaire_update_detection'];

const ATTACHMENT_STATUS_UI = {
  success: { icon: '✓', variant: 'success', label: 'Extracted' },
  pending: { icon: '⏳', variant: 'info', label: 'Pending' },
  failed: { icon: '✗', variant: 'danger', label: 'Failed' },
  unsupported: { icon: '⊘', variant: 'warning', label: 'Unsupported' },
  skipped: { icon: '⊘', variant: 'warning', label: 'Skipped' },
  blocked: { icon: '🔒', variant: 'warning', label: 'Blocked' },
  superseded: { icon: '↻', variant: 'default', label: 'Replaced' },
};

const ATTACHMENT_SOURCE_LABELS = {
  closeio_attachment: 'Email attachment',
  google_doc: 'Google Doc',
  google_sheet: 'Google Sheet',
  google_slide: 'Google Slides',
  google_drive_file: 'Google Drive file',
  manual_upload: 'Manual upload',
};

const BLOCKED_STATUS = 'blocked_pending_drive_connection';

const LeadDetails = ({ leadId, onClose, onRefresh, onScheduleMeeting }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can, isAdmin, is } = usePermissions();
  const canScheduleMeeting = !is.viewer() && !is.complianceOfficer();
  const canModifyMeeting = !is.viewer() && !is.complianceOfficer();
  // Read-only oversight roles (viewer, compliance_officer) hold no proposal write
  // permission, so all proposal-pipeline actions are hidden for them.
  const canManageProposals = can.generateProposal() || can.approveProposal() || can.submitForReview() || can.sendToClient();
  const { user } = useAuth();
  const { isSuperAdmin, activeOrg } = useOrg();
  const toast = useToast();
  const showImpersonationBanner = isSuperAdmin && !!activeOrg;
  // Banner is py-2 (~8px top+bottom) + one text line (~24px) ≈ 40px; header bar is 65px
  const stickyTop = showImpersonationBanner ? 'top-[105px]' : 'top-[65px]';

  // Read initial tab from URL, default to 'research'
  const tabParam = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabParam) ? tabParam : 'research';

  const setActiveTab = useCallback((tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === 'research') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const [lead, setLead] = useState(null);
  // Reported upward by DiligenceCard through its onStatusChange prop. It drives
  // the stepper's first step. Null until the card's first read answers.
  const [diligenceStatus, setDiligenceStatus] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [emails, setEmails] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meetingsLoading, setMeetingsLoading] = useState(false);
  const [emailsLoading, setEmailsLoading] = useState(false);
  // false when the org has no live email capture path (Close, or connected Attio email access); null until known.
  // Describes new capture only: stored emails are always shown.
  const [emailCapture, setEmailCapture] = useState(null);
  const [expandedEmailId, setExpandedEmailId] = useState(null);
  const [attachmentTexts, setAttachmentTexts] = useState({}); // { [attachmentId]: { loading, text, error } }
  const [expandedAttachmentIds, setExpandedAttachmentIds] = useState({}); // { [attachmentId]: true }
  const [retryingEmailId, setRetryingEmailId] = useState(null);
  const [uploadingEmailId, setUploadingEmailId] = useState(null);
  const emailFileInputRef = useRef(null);
  const pendingUploadContextRef = useRef(null); // { emailId, supersedesAttachmentId }
  const [questionnaireLoading, setQuestionnaireLoading] = useState(false);
  const [error, setError] = useState(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [showTelephonicModal, setShowTelephonicModal] = useState(false);


  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [tierUpdating, setTierUpdating] = useState(false);

  // Proposal pipeline state
  const [decisionEngine, setDecisionEngine] = useState(null);
  const [externalResearch, setExternalResearch] = useState(null);
  const [proposals, setProposals] = useState([]);
  // Start in the loading state so the first paint doesn't render the pipeline
  // cards with empty (null) data before the mount fetch resolves — that caused
  // the Step 1 card to briefly flash its gray "Waiting" placeholder, then swap
  // to its real state. Seed from leadId presence so the spinner can't hang if
  // the component is ever rendered without a leadId (the mount effect that
  // clears this flag is guarded by `if (leadId)`).
  const [proposalsLoading, setProposalsLoading] = useState(Boolean(leadId));
  const latestProposalStatus = useMemo(() => {
    if (proposals.length === 0) return null;
    return [...proposals].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]?.status ?? null;
  }, [proposals]);
  const [selectedPipelineStep, setSelectedPipelineStep] = useState(null);
  const [workerModes, setWorkerModes] = useState(
    WORKER_LIST.reduce((acc, w) => ({ ...acc, [w.type]: true }), {})
  );
  const [showDecline, setShowDecline] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [declineComment, setDeclineComment] = useState('');
  const [followupComment, setFollowupComment] = useState('');
  const [reviewActionLoading, setReviewActionLoading] = useState(false);

  // Proposal generation progress state
  const [generationStatus, setGenerationStatus] = useState(null);
  const [generationProposalId, setGenerationProposalId] = useState(null);
  const [generationWorkersCompleted, setGenerationWorkersCompleted] = useState(0);
  const [generationWorkerOutputs, setGenerationWorkerOutputs] = useState([]);
  const [generationRating, setGenerationRating] = useState(null);
  const [generationRatingReasons, setGenerationRatingReasons] = useState(null);
  const [generationStartTime] = useState(Date.now());
  const [showLowRatingModal, setShowLowRatingModal] = useState(false);
  const [forceGenerating, setForceGenerating] = useState(false);

  // Questionnaire processing status for auto-polling
  const [processingStatus, setProcessingStatus] = useState(null);
  const processingStatusRef = useRef(null);
  processingStatusRef.current = processingStatus;
  const [regenerateLoading, setRegenerateLoading] = useState(false);

  // Meeting inline state
  const [meetingTranscripts, setMeetingTranscripts] = useState({});
  const [meetingTranscriptStatuses, setMeetingTranscriptStatuses] = useState({});
  const meetingTranscriptStatusesRef = useRef(meetingTranscriptStatuses);
  meetingTranscriptStatusesRef.current = meetingTranscriptStatuses;
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [pendingStatusUpdate, setPendingStatusUpdate] = useState(null);
  const [fetchingTranscript, setFetchingTranscript] = useState({});
  const [showManualTranscriptModal, setShowManualTranscriptModal] = useState(null);
  const [manualTranscriptText, setManualTranscriptText] = useState('');
  const [savingManualTranscript, setSavingManualTranscript] = useState(false);
  const [generatingMockTranscript, setGeneratingMockTranscript] = useState({});
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const statusDropdownRef = useRef(null);
  const [showMeetingNotesModal, setShowMeetingNotesModal] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [savingMeetingNotes, setSavingMeetingNotes] = useState(false);
  const [viewTranscriptModal, setViewTranscriptModal] = useState(null);

  // Reprocess state — per-item tracking
  const [emailReprocessStates, setEmailReprocessStates] = useState({}); // { [emailId]: { status: 'error', error } }
  const [meetingReprocessStates, setMeetingReprocessStates] = useState({}); // { [meetingId]: { notes: { status, prevTimestamp, error }, transcript: { ... } } }

  // Email bulk selection
  const [selectedEmailIds, setSelectedEmailIds] = useState(new Set());

  // Polling state - for silent background refreshes
  const [isPolling, setIsPolling] = useState(false);

  const fetchLeadDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await leadAPI.getLead(leadId);
      // Backend returns {data: {...}, meta: {...}}
      if (response.data) {
        setLead(response.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load lead details');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  // Silent fetch for polling - no loading state changes
  const fetchLeadDetailsSilent = useCallback(async () => {
    setIsPolling(true);
    try {
      const response = await leadAPI.getLead(leadId);
      if (response.data) {
        setLead(response.data);
      }
    } catch (err) {
      // Log but don't show error to user during polling
      console.error('Polling refresh failed:', err);
    } finally {
      setIsPolling(false);
    }
  }, [leadId]);

  const fetchMeetings = useCallback(async () => {
    setMeetingsLoading(true);
    try {
      const response = await leadAPI.getLeadMeetings(leadId);
      // Backend returns {data: {meetings: [...]}, meta: {...}}
      if (response.data && response.data.meetings) {
        setMeetings(response.data.meetings);
      } else {
        setMeetings([]);
      }
    } catch (err) {
      console.error('Failed to load meetings:', err);
      setMeetings([]);
    } finally {
      setMeetingsLoading(false);
    }
  }, [leadId]);

  // Silent fetch for polling - no loading state changes
  const fetchMeetingsSilent = useCallback(async () => {
    try {
      const response = await leadAPI.getLeadMeetings(leadId);
      if (response.data && response.data.meetings) {
        setMeetings(response.data.meetings);
      }
    } catch {
      // Swallow errors during silent polling
    }
  }, [leadId]);

  const fetchEmails = useCallback(async () => {
    setEmailsLoading(true);
    try {
      const response = await leadAPI.getLeadEmails(leadId);
      if (response.data && response.data.emails) {
        setEmails(response.data.emails);
      } else {
        setEmails([]);
      }
    } catch (err) {
      console.error('Failed to load emails:', err);
      setEmails([]);
    } finally {
      setEmailsLoading(false);
    }
  }, [leadId]);

  const fetchEmailsSilent = useCallback(async () => {
    try {
      const response = await leadAPI.getLeadEmails(leadId);
      if (response.data && response.data.emails) {
        setEmails(response.data.emails);
      }
    } catch {
      // Swallow errors during silent polling
    }
  }, [leadId]);

  // Emails: after 202, backend sets processing_status=PENDING; poll clears it
  const handleReprocessEmail = useCallback(async (emailId, e) => {
    e.stopPropagation();
    setEmailReprocessStates(s => { const n = { ...s }; delete n[emailId]; return n; }); // clear any prior error
    try {
      await leadAPI.reprocessLeadEmail(leadId, emailId);
      await fetchEmailsSilent(); // backend already set PENDING; poll loop kicks in from useEffect
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reprocess failed';
      setEmailReprocessStates(s => ({ ...s, [emailId]: { status: 'error', error: msg } }));
    }
  }, [leadId, fetchEmailsSilent]);

  const handleBulkProcessEmails = useCallback(async () => {
    const ids = [...selectedEmailIds];
    if (ids.length === 0) return;
    setSelectedEmailIds(new Set());
    const results = await Promise.allSettled(
      ids.map(id => leadAPI.reprocessLeadEmail(leadId, id))
    );
    let queued = 0;
    let failed = 0;
    const errors = {};
    results.forEach((result, i) => {
      const id = ids[i];
      if (result.status === 'fulfilled') {
        queued++;
      } else {
        failed++;
        const msg = result.reason?.response?.data?.detail || 'Reprocess failed';
        errors[id] = { status: 'error', error: msg };
      }
    });
    if (Object.keys(errors).length > 0) {
      setEmailReprocessStates(s => ({ ...s, ...errors }));
    }
    if (queued > 0) {
      toast.success(`${queued} queued${failed > 0 ? `, ${failed} failed` : ''}`);
      await fetchEmailsSilent();
    } else {
      toast.error(`${failed} failed`);
    }
  }, [leadId, selectedEmailIds, fetchEmailsSilent, toast]);

  // Meetings: no backend status column — keep 'processing' state, record prev timestamp,
  // poll via fetchMeetingsSilent, clear state when timestamp changes (see effect below)
  const handleReprocessMeetingNotes = useCallback(async (meetingId) => {
    const prevTimestamp = meetings.find(m => m.id === meetingId)?.notes_last_processed_at ?? null;
    setMeetingReprocessStates(s => ({
      ...s,
      [meetingId]: { ...(s[meetingId] || {}), notes: { status: 'processing', prevTimestamp } },
    }));
    try {
      await leadAPI.reprocessMeetingNotes(leadId, meetingId);
      // Stay in 'processing' — the poll effect will clear it when timestamp changes
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reprocess failed';
      setMeetingReprocessStates(s => ({
        ...s,
        [meetingId]: { ...(s[meetingId] || {}), notes: { status: 'error', error: msg } },
      }));
    }
  }, [leadId, meetings]);

  const handleReprocessMeetingTranscript = useCallback(async (meetingId) => {
    const prevTimestamp = meetings.find(m => m.id === meetingId)?.transcript_last_processed_at ?? null;
    setMeetingReprocessStates(s => ({
      ...s,
      [meetingId]: { ...(s[meetingId] || {}), transcript: { status: 'processing', prevTimestamp } },
    }));
    try {
      await leadAPI.reprocessMeetingTranscript(leadId, meetingId);
      // Stay in 'processing' — the poll effect will clear it when timestamp changes
    } catch (err) {
      const msg = err.response?.data?.detail || 'Reprocess failed';
      setMeetingReprocessStates(s => ({
        ...s,
        [meetingId]: { ...(s[meetingId] || {}), transcript: { status: 'error', error: msg } },
      }));
    }
  }, [leadId, meetings]);

  const handleRetryEmailAttachments = useCallback(async (emailId) => {
    setRetryingEmailId(emailId);
    try {
      await leadAPI.retryEmailAttachments(leadId, emailId);
      toast.success('Retry scheduled — refreshing status...');
      setTimeout(() => { fetchEmails(); }, 1500);
    } catch (err) {
      const msg = err.response?.data?.detail?.error?.message || 'Retry failed';
      toast.error(msg);
    } finally {
      setRetryingEmailId(null);
    }
  }, [leadId, fetchEmails, toast]);

  const openEmailFilePicker = useCallback((emailId, supersedesAttachmentId = null) => {
    pendingUploadContextRef.current = { emailId, supersedesAttachmentId };
    if (emailFileInputRef.current) {
      emailFileInputRef.current.value = '';
      emailFileInputRef.current.click();
    }
  }, []);

  const handleEmailFileSelected = useCallback(async (event) => {
    const file = event.target?.files?.[0];
    const ctx = pendingUploadContextRef.current;
    pendingUploadContextRef.current = null;
    if (!file || !ctx) return;
    const { emailId, supersedesAttachmentId } = ctx;
    setUploadingEmailId(emailId);
    try {
      await leadAPI.uploadEmailAttachment(leadId, emailId, file, { supersedesAttachmentId });
      toast.success('Document uploaded — extracting...');
      setTimeout(() => { fetchEmails(); }, 1200);
    } catch (err) {
      const msg = err.response?.data?.detail?.error?.message || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploadingEmailId(null);
    }
  }, [leadId, fetchEmails, toast]);

  const handleToggleAttachmentText = useCallback(async (emailId, attachment) => {
    const attachmentId = attachment.id;
    const alreadyOpen = !!expandedAttachmentIds[attachmentId];
    if (alreadyOpen) {
      setExpandedAttachmentIds((prev) => {
        const next = { ...prev };
        delete next[attachmentId];
        return next;
      });
      return;
    }
    setExpandedAttachmentIds((prev) => ({ ...prev, [attachmentId]: true }));
    if (attachmentTexts[attachmentId]?.text || attachmentTexts[attachmentId]?.error) {
      return;
    }
    setAttachmentTexts((prev) => ({
      ...prev,
      [attachmentId]: { loading: true, text: null, error: null },
    }));
    try {
      const response = await leadAPI.getEmailAttachmentText(leadId, emailId, attachmentId);
      const text = response?.data?.extracted_text || '';
      setAttachmentTexts((prev) => ({
        ...prev,
        [attachmentId]: { loading: false, text, error: null },
      }));
    } catch (err) {
      const msg = err.response?.data?.detail?.error?.message || 'Failed to load text';
      setAttachmentTexts((prev) => ({
        ...prev,
        [attachmentId]: { loading: false, text: null, error: msg },
      }));
    }
  }, [leadId, expandedAttachmentIds, attachmentTexts]);

  const fetchQuestionnaire = useCallback(async () => {
    setQuestionnaireLoading(true);
    try {
      const response = await leadAPI.getLeadQuestionnaire(leadId);
      if (response.data) {
        setQuestionnaire(response.data);
      } else {
        setQuestionnaire(null);
      }
    } catch (err) {
      // 404 is expected if questionnaire not generated yet
      if (err.response?.status !== 404) {
        console.error('Failed to load questionnaire:', err);
      }
      setQuestionnaire(null);
    } finally {
      setQuestionnaireLoading(false);
    }
  }, [leadId]);

  const fetchProposalData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setProposalsLoading(true);
    try {
      const [deRes, erRes, propRes] = await Promise.allSettled([
        getDecisionEngineOutput(leadId),
        getExternalResearch(leadId),
        getLeadProposals(leadId),
      ]);
      if (deRes.status === 'fulfilled' && deRes.value?.data) {
        setDecisionEngine(deRes.value.data);
      }
      if (erRes.status === 'fulfilled' && erRes.value?.data) {
        setExternalResearch(erRes.value.data);
      }
      if (propRes.status === 'fulfilled' && propRes.value?.data) {
        setProposals(propRes.value.data.proposals || propRes.value.data || []);
      }

      // Auto-select the latest pipeline step on initial load
      if (!silent) {
        const fetchedProposals = propRes.status === 'fulfilled' ? (propRes.value?.data?.proposals || propRes.value?.data || []) : [];
        const deData = deRes.status === 'fulfilled' ? deRes.value?.data : null;
        const generatingStatuses = ['queued', 'workers_running', 'generating_proposal', 'rating_proposal', 'rating_below_threshold'];
        const hasGenerating = fetchedProposals.some(p => generatingStatuses.includes(p.generation_status));
        const hasFailed = fetchedProposals.some(p => p.generation_status === 'failed');
        if (hasGenerating) {
          const target = fetchedProposals.find(p => generatingStatuses.includes(p.generation_status));
          setGenerationStatus(target.generation_status);
          setGenerationProposalId(target.id);
          setSelectedPipelineStep('proposals');
        } else if (hasFailed) {
          const target = fetchedProposals.find(p => p.generation_status === 'failed');
          setGenerationStatus('failed');
          setGenerationProposalId(target.id);
          setSelectedPipelineStep('proposals');
        } else if (fetchedProposals.length > 0) {
          setSelectedPipelineStep('proposals');
        } else if (deData) {
          setSelectedPipelineStep('analysis');
        }
      }
    } catch (err) {
      console.error('Failed to load proposal data:', err);
    } finally {
      if (!silent) setProposalsLoading(false);
    }
  }, [leadId]);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const response = await leadAPI.getLeadAnalytics(leadId);
      if (response.data) {
        setAnalytics(response.data);
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [leadId]);

  const handleTierChange = async (newTier) => {
    setTierUpdating(true);
    try {
      await leadAPI.updateLead(leadId, { tier: newTier, trigger_research_refresh: false });
      // Update local state
      setLead(prev => prev ? { ...prev, tier: newTier } : prev);
      setAnalytics(prev => prev ? { ...prev, tier: newTier } : prev);
      toast.success('Lead tier updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update tier');
    } finally {
      setTierUpdating(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (leadId) {
      fetchLeadDetails();
      fetchMeetings();
      fetchEmails();
      fetchProposalData();
      fetchAnalytics();
      // Load the questionnaire on initial load (not just when the Questionnaire
      // tab is opened) so hasQuestionnaire is correct on any starting tab — e.g.
      // the Meetings-tab "Upload Document" button gates on it. Handles the 404
      // "not generated" case gracefully.
      fetchQuestionnaire();
    }
  }, [leadId, fetchLeadDetails, fetchMeetings, fetchEmails, fetchProposalData, fetchAnalytics, fetchQuestionnaire]);

  useEffect(() => {
    if (activeTab === 'questionnaire' && leadId) {
      fetchQuestionnaire();
    }
  }, [activeTab, leadId, fetchQuestionnaire]);

  // Auto-poll proposal pipeline (Decision Engine / External Research) when in progress
  useEffect(() => {
    let intervalId;

    const deStatus = decisionEngine?.status;
    const erStatus = externalResearch?.status;
    const isRunning = (deStatus && ['queued', 'running'].includes(deStatus))
      || (erStatus && ['queued', 'running'].includes(erStatus))
      || (deStatus === 'completed' && !externalResearch);

    if (isRunning) {
      intervalId = setInterval(() => {
        fetchProposalData({ silent: true });
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [decisionEngine, externalResearch, fetchProposalData]);

  // Auto-poll proposal generation progress when generating
  const GENERATION_STATUSES = ['queued', 'workers_running', 'generating_proposal', 'rating_proposal'];
  useEffect(() => {
    if (!generationStatus || !GENERATION_STATUSES.includes(generationStatus)) return;
    let intervalId;

    const checkGeneration = async () => {
      try {
        let targetId = generationProposalId;

        // Only fetch full proposals list when we don't know the proposal ID
        if (!targetId) {
          const proposalsRes = await getLeadProposals(leadId);
          const allProposals = proposalsRes.data?.proposals || [];
          const processing = allProposals.find(p => GENERATION_STATUSES.includes(p.generation_status));
          const belowThreshold = allProposals.find(p => p.generation_status === 'rating_below_threshold');
          const target = processing || belowThreshold;

          if (!target) {
            const completed = allProposals.find(p => p.generation_status === 'completed');
            const failed = allProposals.find(p => p.generation_status === 'failed');
            if (completed) {
              setGenerationStatus('completed');
              setGenerationProposalId(null);
              fetchProposalData();
            } else if (failed) {
              setGenerationStatus('failed');
              setGenerationProposalId(failed.id);
            }
            return;
          }

          targetId = target.id;
          setGenerationProposalId(targetId);
        }

        const res = await getProposalProcessingStatus(leadId, targetId);
        setGenerationStatus(res.data.generation_status || 'queued');
        setGenerationWorkersCompleted(res.data.workers_completed || 0);

        if (res.data.rating != null) {
          setGenerationRating(res.data.rating);
          setGenerationRatingReasons(res.data.rating_reasons || null);
        }

        try {
          const workersRes = await getWorkerOutputs(leadId, targetId);
          setGenerationWorkerOutputs(workersRes.data?.worker_outputs || []);
        } catch { /* optional */ }

        if (res.data.generation_status === 'rating_below_threshold') {
          setShowLowRatingModal(true);
          clearInterval(intervalId);
          return;
        }

        if (!res.data.is_processing && res.data.generation_status === 'completed') {
          setGenerationStatus('completed');
          setGenerationProposalId(null);
          fetchProposalData();
        }
      } catch { /* ignore */ }
    };

    checkGeneration();
    intervalId = setInterval(checkGeneration, 3000);
    return () => clearInterval(intervalId);
  }, [leadId, generationStatus, generationProposalId, fetchProposalData]);

  // Handle force generate for low-rating proposals
  const handleForceGenerate = async () => {
    if (!generationProposalId) return;
    setForceGenerating(true);
    try {
      await forceGenerateProposal(leadId, generationProposalId);
      setShowLowRatingModal(false);
      setGenerationStatus('generating_proposal');
    } catch { /* stay on modal */ }
    finally { setForceGenerating(false); }
  };

  // Handle retry for failed proposal generation
  const handleRetryProposalGeneration = async () => {
    if (!generationProposalId) return;
    setRegenerateLoading(true);
    try {
      await regenerateProposal(leadId, generationProposalId);
      setGenerationStatus('queued');
      toast.success('Proposal regeneration started');
    } catch (err) {
      toast.error('Failed to retry proposal generation');
    } finally {
      setRegenerateLoading(false);
    }
  };

  // Handle questionnaire completion - refresh proposal data and switch to proposals tab
  const handleQuestionnaireComplete = useCallback(() => {
    fetchQuestionnaire();
    toast.success('Questionnaire completed! Analysis starting...');
    // Give backend a moment to create the Decision Engine record, then fetch and switch tab
    setTimeout(() => {
      fetchProposalData();
      setActiveTab('proposals');
    }, 2000);
  }, [fetchQuestionnaire, fetchProposalData, setActiveTab, toast]);

  // Human review action handlers
  const handleReviewProceed = async () => {
    setReviewActionLoading(true);
    try {
      const modes = WORKER_LIST.map(w => ({
        worker_type: w.type,
        mode: workerModes[w.type] ? 'required' : 'optional',
      }));
      await proceedFromProposalReview(leadId, { worker_modes: modes });
      toast.success('Proposal generation started!');
      setGenerationStatus('queued');
      setSelectedPipelineStep('proposals');
      fetchProposalData({ silent: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to proceed');
    } finally {
      setReviewActionLoading(false);
    }
  };

  const handleReviewDecline = async () => {
    setReviewActionLoading(true);
    try {
      await declineFromProposalReview(leadId, { comment: declineComment });
      toast.success('Pipeline declined.');
      setSelectedPipelineStep(null);
      setShowDecline(false);
      setDeclineComment('');
      fetchProposalData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline');
    } finally {
      setReviewActionLoading(false);
    }
  };

  const handleReviewFollowup = async () => {
    setReviewActionLoading(true);
    try {
      await scheduleFollowupFromProposalReview(leadId, { comment: followupComment });
      toast.success('Status updated to Schedule Follow-up');
      setSelectedPipelineStep(null);
      setShowFollowup(false);
      setFollowupComment('');
      fetchProposalData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to schedule follow-up');
    } finally {
      setReviewActionLoading(false);
    }
  };

  // Auto-poll questionnaire processing status when on questionnaire tab and not yet generated
  useEffect(() => {
    let intervalId;
    let isMounted = true;
    let inFlight = false;

    const qStatus = questionnaire?.completion_status?.status;
    // Read processing status via ref so it is NOT an effect dependency —
    // otherwise every polled response (which calls setProcessingStatus) would
    // re-run this effect, recreate the interval and fire an immediate request,
    // making polls run back-to-back instead of every 5 seconds.
    const currentProcessing = processingStatusRef.current;
    const shouldPoll = activeTab === 'questionnaire'
      && (!questionnaire || qStatus === 'not_generated' || qStatus === 'processing' || (currentProcessing && currentProcessing.status === 'processing'))
      && meetings.some(m => m.transcript_status === 'completed');

    const checkProcessingStatus = async () => {
      // Skip if unmounted or a previous request is still in flight (prevents
      // overlapping requests when a poll takes longer than the interval).
      if (!isMounted || inFlight) return;
      inFlight = true;

      try {
        const response = await leadAPI.getQuestionnaireProcessingStatus(leadId);
        if (!isMounted) return;

        const statusData = response.data;
        setProcessingStatus(statusData);

        // If completed, fetch the questionnaire and stop polling
        if (statusData.status === 'completed') {
          fetchQuestionnaire();
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
        // If stuck or failed (and not should_poll), stop polling
        else if ((statusData.status === 'stuck' || statusData.status === 'failed') && !statusData.should_poll) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
        }
      } catch (err) {
        console.error('Failed to check processing status:', err);
      } finally {
        inFlight = false;
      }
    };

    if (shouldPoll) {
      // Check immediately
      checkProcessingStatus();

      // Then poll every 5 seconds
      intervalId = setInterval(checkProcessingStatus, 5000);
    } else {
      // Reset processing status when not polling
      setProcessingStatus(null);
    }

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [activeTab, questionnaire, meetings, leadId, fetchQuestionnaire]);

  // Fetch all meeting transcript statuses when meetings tab is active
  const fetchAllMeetingTranscripts = useCallback(async () => {
    if (meetings.length === 0) return;
    try {
      const statusResults = await Promise.all(
        meetings.map(m =>
          transcriptAPI.getTranscriptStatus(m.id)
            .then(statusRes => ({ meetingId: m.id, data: statusRes.data }))
            .catch(() => null)
        )
      );

      const newStatuses = {};
      const transcriptPromises = [];
      let hasNewlyCompleted = false;

      statusResults.forEach(result => {
        if (!result?.data) return;
        newStatuses[result.meetingId] = result.data;
        if (result.data.has_transcript) {
          // Check if this transcript just became available (was pending/processing before)
          const prev = meetingTranscriptStatusesRef.current[result.meetingId];
          if (!prev || !prev.has_transcript) {
            hasNewlyCompleted = true;
          }
          transcriptPromises.push(
            meetingAPI.getMeetingTranscript(result.meetingId)
              .then(res => ({ meetingId: result.meetingId, data: res.data }))
              .catch(() => null)
          );
        }
      });

      if (Object.keys(newStatuses).length > 0) {
        setMeetingTranscriptStatuses(prev => ({ ...prev, ...newStatuses }));
      }

      // Re-fetch meetings to pick up status changes (e.g. scheduled -> completed)
      if (hasNewlyCompleted) {
        fetchMeetingsSilent();
        fetchLeadDetailsSilent();
      }

      if (transcriptPromises.length > 0) {
        const transcriptResults = await Promise.all(transcriptPromises);
        const newTranscripts = {};
        transcriptResults.forEach(result => {
          if (result?.data) newTranscripts[result.meetingId] = result.data;
        });
        if (Object.keys(newTranscripts).length > 0) {
          setMeetingTranscripts(prev => ({ ...prev, ...newTranscripts }));
        }
      }
    } catch {
      // Swallow errors to match prior behavior
    }
  }, [meetings, leadId, fetchMeetingsSilent, fetchLeadDetailsSilent]);

  useEffect(() => {
    if (activeTab === 'meetings' && meetings.length > 0) {
      fetchAllMeetingTranscripts();
    }
  }, [activeTab, fetchAllMeetingTranscripts, meetings.length]);

  // Poll transcript statuses when any meeting has pending/processing transcripts
  useEffect(() => {
    if (activeTab !== 'meetings' || meetings.length === 0) return;

    const hasPendingTranscripts = Object.values(meetingTranscriptStatuses).some(
      s => s && !s.has_transcript && (s.transcript_status === 'pending' || s.transcript_status === 'processing')
    );

    if (!hasPendingTranscripts) return;

    const intervalId = setInterval(() => {
      fetchAllMeetingTranscripts();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [activeTab, meetings.length, meetingTranscriptStatuses, fetchAllMeetingTranscripts]);

  // Email capture needs Close, or a live Attio email access connection. Other orgs get no emails.
  useEffect(() => {
    if (!activeOrg?.id) return;
    let cancelled = false;
    organizationAPI.getOrgCrm(activeOrg.id)
      .then((crm) => {
        if (!cancelled) setEmailCapture(crm?.capabilities?.email_capture ?? null);
      })
      .catch(() => {
        // Unknown: keep the email card as it was
        if (!cancelled) setEmailCapture(null);
      });
    return () => { cancelled = true; };
  }, [activeOrg?.id]);

  // Poll emails every 5s while any email has processing_status === 'pending'
  useEffect(() => {
    const hasPendingEmail = emails.some(e => e.processing_status === 'pending');
    if (!hasPendingEmail) return;
    const id = setInterval(fetchEmailsSilent, 5000);
    return () => clearInterval(id);
  }, [emails, fetchEmailsSilent]);

  // Poll meetings every 5s while any meeting reprocess is in-progress
  useEffect(() => {
    const hasActive = Object.values(meetingReprocessStates).some(
      s => s.notes?.status === 'processing' || s.transcript?.status === 'processing'
    );
    if (!hasActive) return;
    const id = setInterval(fetchMeetingsSilent, 5000);
    return () => clearInterval(id);
  }, [meetingReprocessStates, fetchMeetingsSilent]);

  // Clear meeting reprocess states when server timestamps change (processing complete)
  useEffect(() => {
    setMeetingReprocessStates(prev => {
      let changed = false;
      const next = { ...prev };
      meetings.forEach(meeting => {
        const state = next[meeting.id];
        if (!state) return;
        if (state.notes?.status === 'processing') {
          const prevTs = state.notes.prevTimestamp;
          if (meeting.notes_last_processed_at !== prevTs) {
            next[meeting.id] = { ...next[meeting.id], notes: { status: 'idle' } };
            changed = true;
          }
        }
        if (state.transcript?.status === 'processing') {
          const prevTs = state.transcript.prevTimestamp;
          if (meeting.transcript_last_processed_at !== prevTs) {
            next[meeting.id] = { ...next[meeting.id], transcript: { status: 'idle' } };
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [meetings]);

  // Handle retry questionnaire generation
  const handleRetryGeneration = async () => {
    setRegenerateLoading(true);
    try {
      await leadAPI.regenerateQuestionnaire(leadId);
      toast.info('Questionnaire regeneration started...');
      // Reset processing status to trigger polling again
      setProcessingStatus({ is_processing: true, status: 'processing', should_poll: true });
    } catch (err) {
      console.error('Failed to regenerate questionnaire:', err);
      toast.error(err.message || 'Failed to start questionnaire generation');
    } finally {
      setRegenerateLoading(false);
    }
  };


  // Check if user can edit this lead
  const canEditLead = useCallback(() => {
    if (!lead || !user) return false;

    // Admin can edit any lead
    if (isAdmin) return true;

    // BD Team can edit leads they created or are assigned to
    if (is.bdTeam() && can.updateLead()) {
      return lead.created_by === user.id || lead.assigned_to === user.id;
    }

    return false;
  }, [lead, user, isAdmin, is, can]);

  // Handle edit mode toggle
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
  };

  const handleEditSuccess = (updatedLead) => {
    setLead(updatedLead);
    setIsEditMode(false);

    // Show success notification
    toast.success('Lead updated successfully');

    // Notify parent component
    onRefresh && onRefresh();
  };

  // Only show full-page loading spinner on initial load (when no lead data exists)
  if (loading && !lead) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading lead details...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error || !lead) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error || 'Lead not found'}</p>
            <Button onClick={onClose}>Close</Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { variant: 'info', label: 'New' },
      meeting_scheduled: { variant: 'accent', label: 'Meeting Scheduled' },
      contacted: { variant: 'warning', label: 'Contacted' },
      followup: { variant: 'warning', label: 'Follow Up' },
      questionnaire_completed: { variant: 'primary', label: 'Questionnaire Completed' },
      pre_proposal: { variant: 'primary', label: 'Pre-Proposal' },
      proposal_generation: { variant: 'success', label: 'Proposal Generation' },
      qualified: { variant: 'success', label: 'Qualified' },
      converted: { variant: 'success', label: 'Converted' },
    };
    const config = statusConfig[status] || { variant: 'default', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getSourceTypeBadge = (sourceType) => {
    const sourceConfig = {
      mock: { variant: 'warning', label: 'Mock' },
      crm: { variant: 'accent', label: 'CRM' },
      manual: { variant: 'primary', label: 'Manual' },
    };
    const config = sourceConfig[sourceType] || { variant: 'default', label: sourceType || 'Unknown' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDuration = (totalSeconds) => {
    if (!totalSeconds || totalSeconds <= 0) return '0s';
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 && days === 0) parts.push(`${seconds}s`);
    return parts.join(' ') || '0s';
  };

  const tierConfig = {
    tier_1: { label: 'Tier 1', color: 'text-green-700 bg-green-50 border-green-200' },
    tier_2: { label: 'Tier 2', color: 'text-blue-700 bg-blue-50 border-blue-200' },
    tier_3: { label: 'Tier 3', color: 'text-teal-700 bg-teal-50 border-teal-200' },
    tier_4: { label: 'Tier 4', color: 'text-orange-700 bg-orange-50 border-orange-200' },
    tier_5: { label: 'Tier 5', color: 'text-gray-700 bg-gray-50 border-gray-200' },
  };

  // Compute step completion status for the stepper
  const getCompletionStatus = () => {
    const status = {};
    // Due Diligence. The step key stays 'research' because it is also the tab's
    // URL value — see LeadStepper.js.
    status.research = DILIGENCE_COMPLETE_STATUSES.includes(diligenceStatus)
      ? 'completed'
      : 'upcoming';
    // Meetings
    status.meetings = meetings.some(m => m.transcript_status === 'completed') ? 'completed' : 'upcoming';
    // Questionnaire
    status.questionnaire = questionnaire && questionnaire.completion_status?.status === 'completed' ? 'completed' : 'upcoming';
    // Proposals — only completed if at least one proposal is approved or pending approval
    const proposalCompletedStatuses = ['approved', 'pending_qa', 'sent_to_client', 'accepted'];
    status.proposals = proposals.some(p => proposalCompletedStatuses.includes(p.status))
      ? 'completed' : 'upcoming';

    // Mark the first upcoming step as in_progress (current stage)
    const order = ['research', 'meetings', 'questionnaire', 'proposals'];
    for (const key of order) {
      if (status[key] === 'upcoming') {
        status[key] = 'in_progress';
        break;
      }
    }
    return status;
  };

  const handleStepperTabChange = (tab) => {
    if (tab === 'proposals') {
      // Refresh silently so the already-loaded pipeline section isn't collapsed
      // into the loading spinner and re-rendered on every tab switch.
      fetchProposalData({ silent: true });
    }
    setActiveTab(tab);
  };

  // --- Inline meeting expansion helpers ---

  const fetchMeetingTranscriptData = async (meetingId) => {
    try {
      const statusRes = await transcriptAPI.getTranscriptStatus(meetingId);
      if (statusRes.data) {
        setMeetingTranscriptStatuses(prev => ({ ...prev, [meetingId]: statusRes.data }));
        if (statusRes.data.has_transcript) {
          const transcriptRes = await meetingAPI.getMeetingTranscript(meetingId);
          if (transcriptRes.data) {
            setMeetingTranscripts(prev => ({ ...prev, [meetingId]: transcriptRes.data }));
          }
        }
      }
    } catch (err) {
      console.log('Transcript not available for meeting:', meetingId);
    }
  };

  const handleViewTranscript = (meetingId) => {
    setViewTranscriptModal(meetingId);
  };

  const handleMeetingStatusClick = (meetingId, newStatus) => {
    setPendingStatusUpdate({ meetingId, status: newStatus });
    setShowStatusModal(true);
  };

  const handleConfirmMeetingStatus = async () => {
    if (!pendingStatusUpdate) return;
    setStatusUpdateLoading(true);
    try {
      await leadAPI.updateMeetingStatus(pendingStatusUpdate.meetingId, { status: pendingStatusUpdate.status });
      setMeetings(prev => prev.map(m =>
        m.id === pendingStatusUpdate.meetingId ? { ...m, status: pendingStatusUpdate.status } : m
      ));
      setShowStatusModal(false);
      setPendingStatusUpdate(null);
      toast.success('Meeting status updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleFetchMeetingTranscript = async (meetingId) => {
    setFetchingTranscript(prev => ({ ...prev, [meetingId]: true }));
    try {
      await leadAPI.fetchTranscriptManually(leadId, meetingId);
      toast.success('Transcript fetch initiated...');
      setTimeout(() => fetchMeetingTranscriptData(meetingId), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch transcript');
    } finally {
      setFetchingTranscript(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const handleSaveManualMeetingTranscript = async () => {
    if (!manualTranscriptText.trim() || !showManualTranscriptModal) return;
    setSavingManualTranscript(true);
    try {
      await leadAPI.saveManualTranscript(leadId, showManualTranscriptModal, { transcript_text: manualTranscriptText });
      toast.success('Transcript saved');
      setShowManualTranscriptModal(null);
      setManualTranscriptText('');
      setMeetings(prev => prev.map(m =>
        m.id === showManualTranscriptModal ? { ...m, status: 'completed', transcript_status: 'completed' } : m
      ));
      await fetchMeetingTranscriptData(showManualTranscriptModal);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save transcript');
    } finally {
      setSavingManualTranscript(false);
    }
  };

  const handleGenerateMockMeetingTranscript = async (meetingId) => {
    setGeneratingMockTranscript(prev => ({ ...prev, [meetingId]: true }));
    try {
      await mockAPI.generateMockTranscript(leadId, meetingId);
      toast.success('Mock transcript generated');
      setMeetings(prev => prev.map(m =>
        m.id === meetingId ? { ...m, status: 'completed', transcript_status: 'completed' } : m
      ));
      await fetchMeetingTranscriptData(meetingId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate mock transcript');
    } finally {
      setGeneratingMockTranscript(prev => ({ ...prev, [meetingId]: false }));
    }
  };

  const handleOpenMeetingNotes = (meetingId) => {
    const transcript = meetingTranscripts[meetingId];
    setMeetingNotes(transcript?.meeting_notes || '');
    setShowMeetingNotesModal(meetingId);
  };

  const handleSaveMeetingNotes = async () => {
    if (!showMeetingNotesModal) return;
    setSavingMeetingNotes(true);
    try {
      const res = await transcriptAPI.updateMeetingNotes(leadId, showMeetingNotesModal, meetingNotes);
      if (res.data) {
        setMeetingTranscripts(prev => ({
          ...prev,
          [showMeetingNotesModal]: { ...prev[showMeetingNotesModal], meeting_notes: res.data.notes }
        }));
        toast.success('Notes saved');
        setShowMeetingNotesModal(null);
      }
    } catch (err) {
      toast.error('Failed to save notes');
    } finally {
      setSavingMeetingNotes(false);
    }
  };


  return (
    <div className="space-y-3">
      {/* Profile Header — always visible */}
      <Card>
        <CardBody className="!py-3">
          {isEditMode ? (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Lead Information</h3>
              <LeadEditForm
                lead={lead}
                onSuccess={handleEditSuccess}
                onCancel={handleCancelEdit}
              />
            </div>
          ) : (
            <>
              <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-primary">
                    {lead.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center flex-wrap gap-x-2.5 gap-y-1 mb-0.5 min-w-0">
                      <h2 className="text-xl font-sans font-semibold text-gray-900 dark:text-white break-words">{lead.name}</h2>
                      {getStatusBadge(lead.status)}
                      {lead.lead_source_type && getSourceTypeBadge(lead.lead_source_type)}
                      {isPolling && (
                        <Badge variant="default" className="animate-pulse">
                          <span className="flex items-center gap-1">
                            <div className="h-2 w-2 bg-primary rounded-full"></div>
                            Syncing
                          </span>
                        </Badge>
                      )}
                    </div>
                    {canEditLead() && (
                      <Button size="sm" variant="primary" onClick={handleEditClick} className="shrink-0">
                        <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                      {lead.designation}{lead.designation && lead.company ? ' at ' : ''}<span className="font-medium text-gray-800 dark:text-gray-200">{lead.company}</span>
                    </p>
                    {/* Quick contact info */}
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5 mt-2 text-sm text-gray-500">
                      {lead.email && (
                        <span className="flex items-center gap-1.5 min-w-0 break-all">
                          <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                          {lead.email}
                        </span>
                      )}
                      {lead.phone && (
                        <span className="flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                          {lead.phone}
                        </span>
                      )}
                      {lead.website && (
                        <a href={lead.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-primary hover:underline">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                          Website
                        </a>
                      )}
                      {lead.linkedin && (
                        <a href={lead.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-primary hover:underline">
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                          LinkedIn
                        </a>
                      )}
                      {lead.last_activity && (
                        <span className="flex items-center gap-1.5" title={`Last Activity: ${new Date(lead.last_activity).toLocaleString()}`}>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          {new Date(lead.last_activity).toLocaleDateString()}
                        </span>
                      )}
                      {lead.notes && (
                        <button
                          onClick={() => setNotesModalOpen(true)}
                          className="flex items-center gap-1.5 text-gray-500 hover:text-primary transition-colors cursor-pointer"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                          Notes
                        </button>
                      )}
                    </div>
                  </div>
                </div>
            </>
          )}
        </CardBody>
      </Card>

      {/* Analytics Strip — always visible */}
      {!isEditMode && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
          <div className="bg-gradient-to-b from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-300 dark:border-gray-600 p-3 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Turnaround Time
            </p>
            {analyticsLoading ? (
              <div className="h-5 bg-gray-100 rounded animate-pulse"></div>
            ) : analytics ? (
              <>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {formatDuration(analytics.qualification_time.elapsed_seconds)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">since lead created</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">--</p>
            )}
          </div>

          <div className="bg-gradient-to-b from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-300 dark:border-gray-600 p-3 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <svg className="w-3 h-3 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              Processing Time
            </p>
            {analyticsLoading ? (
              <div className="h-5 bg-gray-100 rounded animate-pulse"></div>
            ) : analytics ? (
              <>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {formatDuration(analytics.system_processing_time.total_seconds)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">since first meeting</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">--</p>
            )}
          </div>

          <div className="bg-gradient-to-b from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-300 dark:border-gray-600 p-3 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              Total Cost
            </p>
            {analyticsLoading ? (
              <div className="h-5 bg-gray-100 rounded animate-pulse"></div>
            ) : analytics ? (
              <>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  ${analytics.estimated_cost.total_usd.toFixed(2)}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">estimated AI spend</p>
              </>
            ) : (
              <p className="text-sm text-gray-400">--</p>
            )}
          </div>

          <div className="bg-gradient-to-b from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-300 dark:border-gray-600 p-3 shadow-sm">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
              <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" /></svg>
              Current Status
            </p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {lead.status === 'meeting_scheduled'
                ? 'Meeting Scheduled'
                : lead.status ? lead.status.replace(/_/g, '-').replace(/\b\w/g, c => c.toUpperCase()) : '--'}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {tierConfig[lead.tier || analytics?.tier || 'tier_5']?.label || 'Tier 5'}
            </p>
          </div>
        </div>
      )}

      {/* Stepper Navigation */}
      {!isEditMode && (
        <div className={`sticky ${stickyTop} z-20 bg-[#F9FAFB] dark:bg-[#1A1C1E] py-3 -mx-1 px-1`}>
          <LeadStepper
            activeTab={activeTab}
            onTabChange={handleStepperTabChange}
            completionStatus={getCompletionStatus()}
          />
        </div>
      )}

      {/* ===== Tab Content ===== */}

      {!isEditMode && activeTab === 'research' && (
        <div className="space-y-6">
          {/* The tab holds the diligence report itself. DiligenceCard renders
              the PDF when a run produced one, and one tall panel explaining
              the state when it did not. It hides itself when the feature is
              switched off on this deployment.

              The tab's URL value is still `research`, so saved links keep
              working. See the note in LeadStepper.js. */}
          <DiligenceCard
            leadId={leadId}
            companyName={lead.company}
            leadWebsite={lead.website}
            onRunSettled={fetchAnalytics}
            onStatusChange={setDiligenceStatus}
          />
        </div>
      )}

      {!isEditMode && activeTab === 'meetings' && (
        <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Meetings</h3>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                {can.createMeeting() && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowTelephonicModal(true)}
                    className="w-full sm:w-auto justify-center whitespace-nowrap"
                  >
                    + Record Conversation
                  </Button>
                )}
                {canScheduleMeeting && onScheduleMeeting && (
                  <Button size="sm" variant="primary" onClick={() => onScheduleMeeting(leadId, lead.name)} className="w-full sm:w-auto justify-center whitespace-nowrap">
                    + Schedule New Meeting
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {meetingsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading meetings...</p>
              </div>
            ) : meetings.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">No meetings scheduled yet</p>
                {canScheduleMeeting && onScheduleMeeting && (
                  <Button variant="primary" onClick={() => onScheduleMeeting(leadId, lead.name)}>
                    Schedule First Meeting
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {meetings.map((meeting) => {
                  const tStatus = meetingTranscriptStatuses[meeting.id];
                  const tData = meetingTranscripts[meeting.id];
                  const isFinal = ['completed', 'cancelled', 'no_show'].includes(meeting.status);
                  const mRState = meetingReprocessStates[meeting.id] || {};
                  const notesRState = mRState.notes || { status: 'idle' };
                  const transcriptRState = mRState.transcript || { status: 'idle' };
                  const isQuestCompletedMtg = questionnaire?.completion_status?.status === 'completed';
                  const hasTranscript = !!(tStatus?.has_transcript || (tStatus && tStatus.transcript_status === 'completed'));

                  return (
                    <div key={meeting.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      {/* Row 1: Title, Status, Date, Duration, Platform */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-gray-900 dark:text-white text-sm">{meeting.title}</h4>
                        {meeting.type === 'telephonic_conversation' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                            Telephonic
                          </span>
                        )}
                        <Badge variant={meeting.status === 'scheduled' ? 'info' : meeting.status === 'completed' ? 'success' : meeting.status === 'cancelled' ? 'danger' : 'default'}>
                          {meeting.status}
                        </Badge>
                        <PromptVersionsBadge
                          promptsUsed={meeting.prompts_used}
                          currentPromptVersions={meeting.current_prompt_versions}
                          labels={MEETING_PROMPT_LABELS}
                          order={MEETING_PROMPT_ORDER}
                          contextNoun="meeting"
                          align="left"
                        />
                        <div className="flex items-center gap-3 ml-auto text-xs text-gray-500 shrink-0">
                          <span className="flex items-center gap-1" title="Scheduled date & time">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                            {new Date(meeting.scheduled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {meeting.duration_minutes && (
                            <span className="flex items-center gap-1" title="Duration">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                              {meeting.duration_minutes}m
                            </span>
                          )}
                          {meeting.platform && (
                            <span className="hidden md:flex items-center gap-1" title="Platform">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                              {meeting.platform.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          )}
                        </div>
                        {!isFinal && canModifyMeeting && (
                          <div className="relative" ref={statusDropdownOpen === meeting.id ? statusDropdownRef : null}>
                            <button
                              className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                              onClick={() => setStatusDropdownOpen(statusDropdownOpen === meeting.id ? null : meeting.id)}
                              aria-label="Update meeting status"
                              aria-haspopup="menu"
                              aria-expanded={statusDropdownOpen === meeting.id}
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" /></svg>
                            </button>
                            {statusDropdownOpen === meeting.id && (
                              <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-1">
                                <button className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200" onClick={() => { setStatusDropdownOpen(null); handleMeetingStatusClick(meeting.id, 'completed'); }}>
                                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                  Done
                                </button>
                                <button className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200" onClick={() => { setStatusDropdownOpen(null); handleMeetingStatusClick(meeting.id, 'cancelled'); }}>
                                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                  Cancel
                                </button>
                                <button className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200" onClick={() => { setStatusDropdownOpen(null); handleMeetingStatusClick(meeting.id, 'no_show'); }}>
                                  <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>
                                  No Show
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Row 2: Attendees, agenda snippet, meeting link */}
                      <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                        {meeting.attendees && meeting.attendees.length > 0 && (
                          <span className="flex items-center gap-1.5" title="Attendees">
                            <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                            {meeting.attendees.join(', ')}
                          </span>
                        )}
                        {meeting.agenda && (
                          <span className="truncate max-w-xs" title={meeting.agenda}>{meeting.agenda}</span>
                        )}
                        {isHttpUrl(meeting.meeting_link) && canModifyMeeting && (
                          <a href={meeting.meeting_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline ml-auto shrink-0" onClick={e => e.stopPropagation()}>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                            Join
                          </a>
                        )}
                      </div>

                      {/* Row 3: Actions — status updates, transcript actions */}
                      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex-wrap">

                        {/* Transcript actions */}
                        {tStatus?.has_transcript && tData ? (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleViewTranscript(meeting.id)}>
                              <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                              View Transcript
                            </Button>
                            {canModifyMeeting && (
                              <Button size="sm" variant="outline-primary" onClick={() => handleOpenMeetingNotes(meeting.id)}>
                                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
                                {tData.meeting_notes ? 'Edit Notes' : 'Add Notes'}
                              </Button>
                            )}
                          </>
                        ) : tStatus?.has_transcript ? (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                            Loading transcript...
                          </span>
                        ) : (meeting.transcript_status === 'pending' || meeting.transcript_status === 'processing' || meeting.transcript_status === 'completed') && !tStatus ? (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
                            <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                              {meeting.transcript_status === 'completed' ? 'Loading transcript...' : 'AI is generating transcript...'}
                            </span>
                          </div>
                        ) : (
                          <>
                            {meeting.tldv_meeting_id && canModifyMeeting && (
                              <Button size="sm" variant="outline" onClick={() => handleFetchMeetingTranscript(meeting.id)} disabled={fetchingTranscript[meeting.id]}>
                                {fetchingTranscript[meeting.id] ? 'Fetching...' : 'Fetch Transcript'}
                              </Button>
                            )}
                            {canModifyMeeting && (!tStatus?.has_transcript && (tStatus?.transcript_status === 'none' || tStatus?.transcript_status === 'failed' || !tStatus) && !meeting.tldv_meeting_id) && (
                              <>
                                {isDevOrStaging() && (
                                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white border-amber-500" onClick={() => handleGenerateMockMeetingTranscript(meeting.id)} disabled={generatingMockTranscript[meeting.id]}>
                                    {generatingMockTranscript[meeting.id] ? 'Generating...' : 'Mock Transcript'}
                                  </Button>
                                )}
                                <Button size="sm" variant="outline" onClick={() => { setManualTranscriptText(''); setShowManualTranscriptModal(meeting.id); }}>
                                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                  Add Transcript
                                </Button>
                              </>
                            )}
                            {(tStatus && !tStatus.has_transcript && (tStatus.transcript_status === 'pending' || tStatus.transcript_status === 'processing')) && (
                              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg">
                                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="text-sm font-medium text-amber-700 dark:text-amber-300">AI is generating transcript...</span>
                              </div>
                            )}
                          </>
                        )}

                        {/* Reprocess Notes / Transcript buttons */}
                        {can.editQuestionnaire() && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReprocessMeetingNotes(meeting.id)}
                              disabled={notesRState.status === 'processing' || !meeting.notes || isQuestCompletedMtg}
                              title={isQuestCompletedMtg ? 'Reopen questionnaire to reprocess' : !meeting.notes ? 'No notes to reprocess' : 'Reprocess notes'}
                              loading={notesRState.status === 'processing'}
                            >
                              {notesRState.status === 'processing' ? 'Processing...' : 'Reprocess Notes'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReprocessMeetingTranscript(meeting.id)}
                              disabled={transcriptRState.status === 'processing' || !hasTranscript || isQuestCompletedMtg}
                              title={isQuestCompletedMtg ? 'Reopen questionnaire to reprocess' : !hasTranscript ? 'No transcript to reprocess' : 'Reprocess transcript'}
                              loading={transcriptRState.status === 'processing'}
                            >
                              {transcriptRState.status === 'processing' ? 'Processing...' : 'Reprocess Transcript'}
                            </Button>
                          </>
                        )}
                      </div>

                      {/* last_processed_at for notes and transcript */}
                      {(meeting.notes_last_processed_at || meeting.transcript_last_processed_at) && (
                        <div className="mt-1 flex items-center gap-4 text-xs text-gray-400">
                          {meeting.notes_last_processed_at && (
                            <span>Notes: processed {new Date(meeting.notes_last_processed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          )}
                          {meeting.transcript_last_processed_at && (
                            <span>Transcript: processed {new Date(meeting.transcript_last_processed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Email Correspondence */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">Email Correspondence</h3>
                {emails.length > 0 && (
                  <Badge variant="info">{emails.length}</Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {emailsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading emails...</p>
              </div>
            ) : emails.length === 0 && emailCapture === false ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">Email capture is off for this organization.</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  To turn it on, an org admin enables Close, or connects Attio email access on the Attio tab.
                </p>
              </div>
            ) : emails.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">No email correspondence yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {emailCapture === false && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                    Email capture is off, so new emails are not being added. The emails below were saved earlier.
                    To turn capture back on, an org admin enables Close, or reconnects Attio email access on the Attio tab.
                  </div>
                )}
                <input
                  ref={emailFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleEmailFileSelected}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,image/*"
                />
                {/* Email selection toolbar */}
                {can.editQuestionnaire() && emails.length > 0 && (() => {
                  const selectableEmails = emails.filter(e => e.processing_status !== 'pending');
                  const unprocessedEmailIds = new Set(selectableEmails.filter(e => !e.last_processed_at).map(e => e.id));
                  const allEmailsSelected = selectableEmails.length > 0 && selectedEmailIds.size === selectableEmails.length;
                  const unprocessedEmailsSelected = unprocessedEmailIds.size > 0 &&
                    unprocessedEmailIds.size === selectedEmailIds.size &&
                    [...unprocessedEmailIds].every(id => selectedEmailIds.has(id));
                  return (
                    <div className="flex items-center gap-4 pb-1 border-b border-gray-100">
                      <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={allEmailsSelected}
                          onChange={() => setSelectedEmailIds(allEmailsSelected ? new Set() : new Set(selectableEmails.map(e => e.id)))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        All
                      </label>
                      {unprocessedEmailIds.size > 0 && (
                        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={unprocessedEmailsSelected}
                            onChange={() => setSelectedEmailIds(unprocessedEmailsSelected ? new Set() : new Set(unprocessedEmailIds))}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          Unprocessed ({unprocessedEmailIds.size})
                        </label>
                      )}
                      {selectedEmailIds.size > 0 && (
                        <button
                          onClick={handleBulkProcessEmails}
                          disabled={questionnaire?.completion_status?.status === 'completed'}
                          title={questionnaire?.completion_status?.status === 'completed' ? 'Reopen questionnaire to reprocess' : undefined}
                          className="ml-auto px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
                        >
                          Process ({selectedEmailIds.size})
                        </button>
                      )}
                    </div>
                  );
                })()}
                {emails.map((email) => {
                  const attachments = Array.isArray(email.attachments) ? email.attachments : [];
                  const isBlocked = email.processing_status === BLOCKED_STATUS;
                  const isExpanded = expandedEmailId === email.id;
                  const isUploading = uploadingEmailId === email.id;
                  const isRetrying = retryingEmailId === email.id;
                  const emailRState = emailReprocessStates[email.id] || {};
                  const isEmailPending = email.processing_status === 'pending';
                  const isQuestCompleted = questionnaire?.completion_status?.status === 'completed';
                  return (
                    <div
                      key={email.id}
                      className={`border rounded-lg p-3 hover:border-primary hover:shadow-sm transition-all cursor-pointer ${
                        isBlocked ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'
                      }`}
                      onClick={() => setExpandedEmailId(isExpanded ? null : email.id)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        {can.editQuestionnaire() && (
                          <input
                            type="checkbox"
                            checked={selectedEmailIds.has(email.id)}
                            onChange={() => setSelectedEmailIds(prev => {
                              const next = new Set(prev);
                              if (next.has(email.id)) { next.delete(email.id); } else { next.add(email.id); }
                              return next;
                            })}
                            onClick={e => e.stopPropagation()}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={email.direction === 'incoming' ? 'info' : 'success'}>
                              {email.direction === 'incoming' ? 'Received' : 'Sent'}
                            </Badge>
                            <span className="text-sm font-medium text-gray-900 truncate">
                              {email.subject || '(No subject)'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                            {email.source && (
                              <Badge variant="default" className="text-xs">
                                {email.source === 'attio' ? 'Attio' : 'Close'}
                              </Badge>
                            )}
                            <span>{email.sender}</span>
                            {email.email_date && (
                              <span>
                                {new Date(email.email_date).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            )}
                            {email.processing_status === 'processed' && email.questions_updated > 0 && (
                              <Badge variant="success" className="text-xs">
                                {email.questions_updated} Q updated
                              </Badge>
                            )}
                            {isBlocked && (
                              <Badge variant="warning" className="text-xs">Blocked</Badge>
                            )}
                            <PromptVersionsBadge
                              promptsUsed={email.prompts_used}
                              currentPromptVersions={email.current_prompt_versions}
                              labels={EMAIL_PROMPT_LABELS}
                              order={EMAIL_PROMPT_ORDER}
                              contextNoun="email"
                              align="left"
                            />
                          </div>
                          {attachments.length > 0 && (
                            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                              {attachments.map((att) => {
                                const status = att.extraction_status || 'pending';
                                const ui = ATTACHMENT_STATUS_UI[status] || ATTACHMENT_STATUS_UI.pending;
                                const isSuperseded = status === 'superseded';
                                return (
                                  <span
                                    key={att.id}
                                    className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${
                                      isSuperseded
                                        ? 'border-gray-200 bg-gray-50 text-gray-400 line-through'
                                        : 'border-gray-200 bg-white text-gray-700'
                                    }`}
                                    title={`${ATTACHMENT_SOURCE_LABELS[att.source] || att.source} — ${ui.label}${att.extraction_error ? ': ' + att.extraction_error : ''}`}
                                  >
                                    <span>{ui.icon}</span>
                                    <span className="truncate max-w-[200px]">{att.filename}</span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                          {can.editQuestionnaire() && (
                            <button
                              onClick={(e) => handleReprocessEmail(email.id, e)}
                              disabled={isEmailPending || isQuestCompleted}
                              title={isQuestCompleted ? 'Reopen questionnaire to reprocess' : isEmailPending ? 'Processing...' : 'Reprocess email'}
                              className="p-1 rounded text-gray-400 hover:text-blue-600 transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              {isEmailPending ? (
                                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              )}
                            </button>
                          )}
                          {isEmailPending && (
                            <span className="text-xs text-yellow-600">Processing...</span>
                          )}
                          {!isEmailPending && emailRState.status === 'error' && (
                            <span className="text-xs text-red-500" title={emailRState.error}>Failed</span>
                          )}
                          {!isEmailPending && !emailRState.error && email.last_processed_at && (
                            <span className="text-xs text-gray-400">
                              {new Date(email.last_processed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {!isEmailPending && !emailRState.error && !email.last_processed_at && (
                            <span className="text-xs text-gray-400">Not processed</span>
                          )}
                        </div>
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {isBlocked && (
                        <div
                          className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="font-medium mb-1">Questionnaire update paused</div>
                          <div className="mb-2">
                            {email.blocked_reason ||
                              'A Google Drive link in this email requires authentication to read.'}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => navigate('/profile')}>
                              Connect Drive
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRetryEmailAttachments(email.id)}
                              loading={isRetrying}
                            >
                              Retry
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEmailFilePicker(email.id)}
                              loading={isUploading}
                            >
                              Upload document
                            </Button>
                          </div>
                        </div>
                      )}

                      {isExpanded && (
                        <div
                          className="mt-3 pt-3 border-t border-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {email.body_text && (
                            <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans mb-4">{email.body_text}</pre>
                          )}

                          <div className="flex items-center justify-between mb-2">
                            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                              Attachments &amp; Linked Documents
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEmailFilePicker(email.id)}
                              loading={isUploading}
                            >
                              Upload document
                            </Button>
                          </div>

                          {attachments.length === 0 ? (
                            <p className="text-xs text-gray-500 italic">No attachments or linked documents.</p>
                          ) : (
                            <div className="space-y-2">
                              {attachments.map((att) => {
                                const status = att.extraction_status || 'pending';
                                const ui = ATTACHMENT_STATUS_UI[status] || ATTACHMENT_STATUS_UI.pending;
                                const sourceLabel = ATTACHMENT_SOURCE_LABELS[att.source] || att.source;
                                const canShowText = status === 'success';
                                const canReplace = ['failed', 'blocked', 'unsupported', 'skipped'].includes(status);
                                const isSuperseded = status === 'superseded';
                                const textState = attachmentTexts[att.id];
                                const textOpen = !!expandedAttachmentIds[att.id];
                                return (
                                  <div
                                    key={att.id}
                                    className={`border rounded-md p-2.5 ${
                                      isSuperseded
                                        ? 'border-gray-200 bg-gray-50 opacity-60'
                                        : 'border-gray-200 bg-white'
                                    }`}
                                  >
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className={`text-sm font-medium text-gray-900 ${isSuperseded ? 'line-through' : ''}`}>
                                            {att.filename}
                                          </span>
                                          <Badge variant={ui.variant} className="text-xs">
                                            {ui.icon} {ui.label}
                                          </Badge>
                                          <span className="text-xs text-gray-500">{sourceLabel}</span>
                                          {att.fetched_by_user_email && (
                                            <span className="text-xs text-gray-400">
                                              via {att.fetched_by_user_email}
                                            </span>
                                          )}
                                        </div>
                                        {att.extraction_error && (
                                          <div className="text-xs text-red-600 mt-1">
                                            {att.extraction_error}
                                          </div>
                                        )}
                                        {att.extracted_chars > 0 && (
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {att.extracted_chars.toLocaleString()} characters extracted
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {canShowText && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleToggleAttachmentText(email.id, att)}
                                          >
                                            {textOpen ? 'Hide text' : 'Show extracted text'}
                                          </Button>
                                        )}
                                        {canReplace && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => openEmailFilePicker(email.id, att.id)}
                                            loading={isUploading}
                                          >
                                            Replace with upload
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    {textOpen && (
                                      <div className="mt-2 pt-2 border-t border-gray-100">
                                        {textState?.loading ? (
                                          <p className="text-xs text-gray-500 italic">Loading extracted text...</p>
                                        ) : textState?.error ? (
                                          <p className="text-xs text-red-600">{textState.error}</p>
                                        ) : textState?.text ? (
                                          <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">
                                            {textState.text}
                                          </pre>
                                        ) : (
                                          <p className="text-xs text-gray-500 italic">No text available.</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Documents */}
        <DocumentsCard
          leadId={lead.id}
          isCompleted={questionnaire?.completion_status?.status === 'completed'}
          hasQuestionnaire={!!questionnaire && questionnaire.completion_status?.status !== 'not_generated'}
        />
        </div>
      )}

      {!isEditMode && activeTab === 'questionnaire' && (
        <Card>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-sans font-semibold text-gray-900 dark:text-white leading-tight">Discovery Questionnaire</h3>
                  <p className="text-xs text-gray-500">
                    {questionnaire && questionnaire.completion_status?.status !== 'not_generated'
                      ? `${questionnaire.completion_status?.answered_count || 0} of ${questionnaire.total_questions || 33} questions answered`
                      : 'Responses will be auto-filled from meeting transcripts'
                    }
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {/* Status Banner - Show relevant status info */}
            {(!questionnaire || questionnaire.completion_status?.status === 'not_generated') && !questionnaireLoading && (
              <>
                {/* Processing in progress - Blue banner with auto-refresh indicator */}
                {processingStatus?.status === 'processing' && (
                  <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-blue-900">Generating Questionnaire...</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          AI is extracting responses from the meeting transcript.
                        </p>
                        <p className="text-xs text-blue-500 mt-2">
                          Auto-refreshing every 5 seconds
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Processing stuck - Red banner with retry button */}
                {processingStatus?.status === 'stuck' && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-red-900">Processing Issue Detected</h4>
                        <p className="text-sm text-red-700 mt-1">
                          {processingStatus.message || 'Questionnaire generation appears to have stalled.'}
                        </p>
                        <div className="mt-3">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={handleRetryGeneration}
                            disabled={regenerateLoading}
                          >
                            {regenerateLoading ? 'Retrying...' : 'Retry Generation'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Processing failed - Red banner with error and retry */}
                {processingStatus?.status === 'failed' && (
                  <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-red-900">Generation Failed</h4>
                        <p className="text-sm text-red-700 mt-1">
                          {processingStatus.message || 'Failed to generate questionnaire responses.'}
                        </p>
                        {processingStatus.last_error && (
                          <p className="text-xs text-red-500 mt-1 font-mono">
                            Error: {processingStatus.last_error}
                          </p>
                        )}
                        <div className="mt-3">
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={handleRetryGeneration}
                            disabled={regenerateLoading}
                          >
                            {regenerateLoading ? 'Retrying...' : 'Retry Generation'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Default states - Amber banner */}
                {(!processingStatus || processingStatus.status === 'not_started') && (
                  <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-amber-900">
                          {meetings.length === 0 ? 'No Meetings Scheduled' :
                          meetings.some(m => m.transcript_status === 'completed') ? 'Processing Transcript' :
                          'Awaiting Transcript'}
                        </h4>
                        <p className="text-sm text-amber-700 mt-1">
                          {meetings.length === 0
                            ? 'Schedule a meeting to start collecting questionnaire responses automatically.'
                            : meetings.some(m => m.transcript_status === 'completed')
                              ? 'AI will automatically extract responses from the meeting transcript.'
                              : 'Waiting for meeting transcript. Responses will be auto-filled once available.'
                          }
                        </p>
                        <div className="mt-3 flex gap-2">
                          {canScheduleMeeting && meetings.length === 0 && onScheduleMeeting && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => onScheduleMeeting(leadId, lead.name)}
                            >
                              Schedule Meeting
                            </Button>
                          )}
                          {meetings.some(m => m.transcript_status === 'completed') && (
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={handleRetryGeneration}
                              disabled={regenerateLoading}
                            >
                              {regenerateLoading ? 'Starting...' : 'Start Generation'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Always show questionnaire structure */}
            {questionnaireLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading questionnaire...</p>
              </div>
            ) : (
              <DiscoveryQuestionnaire
                leadId={leadId}
                questionnaire={questionnaire}
                onRefresh={fetchQuestionnaire}
                onComplete={handleQuestionnaireComplete}
                latestProposalStatus={latestProposalStatus}
              />
            )}
          </CardBody>
        </Card>
      )}

      {/* Proposals Tab */}
      {!isEditMode && activeTab === 'proposals' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Proposal Pipeline</h3>
              {canManageProposals && questionnaire?.completion_status?.status === 'completed' && !decisionEngine && (
                <Button variant="primary" size="sm" onClick={async () => {
                  try {
                    await triggerDecisionEngine(leadId);
                    toast.success('Decision Engine triggered');
                    fetchProposalData();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Failed to trigger Decision Engine');
                  }
                }}>
                  Run Decision Engine
                </Button>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {proposalsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading proposal data...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Step 1: Analysis & Review */}
                {(() => {
                  const deStatus = decisionEngine?.status;
                  const erStatus = externalResearch?.status;
                  const aiDone = deStatus === 'completed' && erStatus === 'completed';
                  const anyRunning = (deStatus && ['queued', 'running'].includes(deStatus))
                    || (erStatus && ['queued', 'running'].includes(erStatus))
                    || (deStatus === 'completed' && !externalResearch);
                  const anyFailed = deStatus === 'failed' || erStatus === 'failed';
                  const humanDecision = decisionEngine?.human_decision || externalResearch?.human_decision;
                  const allDone = aiDone && humanDecision === 'proceed';

                  const isAnalysisSelected = selectedPipelineStep === 'analysis';
                  const borderClass = allDone ? `border-green-200 bg-green-50 cursor-pointer hover:shadow-md transition-shadow ${isAnalysisSelected ? 'ring-2 ring-green-500 shadow-md' : ''}`
                    : humanDecision === 'decline' ? `border-red-200 bg-red-50 cursor-pointer hover:shadow-md transition-shadow ${isAnalysisSelected ? 'ring-2 ring-red-500 shadow-md' : ''}`
                    : aiDone && !humanDecision ? `border-yellow-200 bg-yellow-50 cursor-pointer hover:shadow-md transition-shadow ${isAnalysisSelected ? 'ring-2 ring-yellow-500 shadow-md' : ''}`
                    : anyRunning ? 'border-blue-200 bg-blue-50'
                    : anyFailed ? `border-red-200 bg-red-50 cursor-pointer hover:shadow-md transition-shadow ${isAnalysisSelected ? 'ring-2 ring-red-500 shadow-md' : ''}`
                    : 'border-gray-200 bg-gray-50';

                  return (
                    <div
                      className={`p-4 rounded-lg border ${borderClass}`}
                      onClick={(aiDone || anyFailed) ? () => setSelectedPipelineStep(prev => prev === 'analysis' ? null : 'analysis') : undefined}
                    >
                      <div className={`flex ${anyRunning || anyFailed ? 'gap-4' : ''}`}>
                        {/* Left column - content (80%) */}
                        <div className={`flex flex-col ${anyRunning || anyFailed ? 'flex-[4]' : 'flex-1'}`}>
                          <div className="flex items-center gap-3">
                            {anyRunning ? (
                              <div className="relative w-8 h-8 flex-shrink-0 flex items-center justify-center">
                                <div className="absolute inset-0 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
                                <span className="text-xs font-bold text-blue-600">1</span>
                              </div>
                            ) : (
                              <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
                                allDone ? 'bg-green-600 text-white'
                                  : humanDecision === 'decline' ? 'bg-red-600 text-white'
                                  : aiDone && !humanDecision ? 'bg-yellow-500 text-white'
                                  : anyFailed ? 'bg-red-600 text-white'
                                  : 'bg-gray-300 text-gray-600'
                              }`}>1</div>
                            )}
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium text-gray-900">Analysis & Human Review</h4>
                              {anyFailed && !anyRunning && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-red-600 text-white rounded-full">
                                  Failed
                                </span>
                              )}
                              {anyRunning && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-blue-600 text-white rounded-full">
                                  <span className="w-1 h-1 bg-white rounded-full animate-pulse" />
                                  Running
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Status description & DE/ER indicators */}
                          <div className="mt-3 flex-1">
                            <p className={`text-sm ${anyRunning ? 'text-blue-700' : 'text-gray-500'}`}>
                              {aiDone
                                ? 'Decision Engine + External Research complete'
                                : anyRunning
                                  ? (deStatus && ['queued', 'running'].includes(deStatus))
                                    ? 'Decision Engine analyzing...'
                                    : 'External research gathering market intelligence...'
                                  : anyFailed
                                    ? 'Analysis failed - can retry'
                                    : questionnaire?.completion_status?.status === 'completed'
                                      ? 'Ready to run'
                                      : 'Waiting for questionnaire completion'}
                            </p>
                            {decisionEngine && (
                              <div className="flex flex-wrap gap-4 mt-2">
                                <span className={`text-sm font-medium flex items-center gap-1.5 ${deStatus === 'completed' ? 'text-green-700' : ['queued', 'running'].includes(deStatus) ? 'text-blue-700' : deStatus === 'failed' ? 'text-red-700' : 'text-gray-600'}`}>
                                  <span className={`w-2 h-2 rounded-full ${deStatus === 'completed' ? 'bg-green-500' : ['queued', 'running'].includes(deStatus) ? 'bg-blue-500 animate-pulse' : deStatus === 'failed' ? 'bg-red-500' : 'bg-gray-400'}`} />
                                  DE: {deStatus === 'running' ? 'Processing' : deStatus === 'queued' ? 'Queued' : deStatus}
                                </span>
                                {externalResearch && (
                                  <span className={`text-sm font-medium flex items-center gap-1.5 ${erStatus === 'completed' ? 'text-green-700' : ['queued', 'running'].includes(erStatus) ? 'text-blue-700' : erStatus === 'failed' ? 'text-red-700' : 'text-gray-600'}`}>
                                    <span className={`w-2 h-2 rounded-full ${erStatus === 'completed' ? 'bg-green-500' : ['queued', 'running'].includes(erStatus) ? 'bg-blue-500 animate-pulse' : erStatus === 'failed' ? 'bg-red-500' : 'bg-gray-400'}`} />
                                    ER: {erStatus === 'running' ? 'Processing' : erStatus === 'queued' ? 'Queued' : erStatus || 'Pending'}
                                  </span>
                                )}
                                <span className={`text-sm font-medium flex items-center gap-1.5 ${
                                  humanDecision === 'proceed' ? 'text-green-700'
                                    : humanDecision === 'decline' ? 'text-red-700'
                                    : aiDone && !humanDecision ? 'text-yellow-700'
                                    : 'text-gray-600'
                                }`}>
                                  <span className={`w-2 h-2 rounded-full ${
                                    humanDecision === 'proceed' ? 'bg-green-500'
                                      : humanDecision === 'decline' ? 'bg-red-500'
                                      : aiDone && !humanDecision ? 'bg-yellow-500 animate-pulse'
                                      : 'bg-gray-400'
                                  }`} />
                                  Review: {humanDecision === 'proceed' ? 'approved' : humanDecision === 'decline' ? 'declined' : aiDone ? 'pending' : 'waiting'}
                                </span>
                              </div>
                            )}
                            {anyRunning && (
                              <div className="mt-3">
                                <div className="w-full bg-blue-100 rounded-full h-1.5 overflow-hidden">
                                  <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: `${deStatus === 'completed' ? 60 : deStatus === 'running' ? 30 : 10}%`, transition: 'width 0.5s ease' }} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Right column - gear animation (20%) */}
                        {anyRunning && (
                          <div className="flex-1 flex items-center justify-center">
                            <svg viewBox="0 0 48 48" fill="none" className="w-full h-full max-w-[80px] max-h-[80px]">
                              {/* Large gear - rotates clockwise */}
                              <g style={{ transformOrigin: '18px 28px', animation: 'spin 3s linear infinite' }}>
                                <circle cx="18" cy="28" r="10" stroke="#2563eb" strokeWidth={1.8} opacity={0.3} />
                                <circle cx="18" cy="28" r="4" stroke="#2563eb" strokeWidth={1.5} />
                                <path strokeLinecap="round" strokeWidth={2} stroke="#2563eb" d="M18 16v3M18 37v3M6 28h3M27 28h3" />
                                <path strokeLinecap="round" strokeWidth={2} stroke="#2563eb" d="M9.5 19.5l2.1 2.1M24.4 34.4l2.1 2.1M9.5 36.5l2.1-2.1M24.4 21.6l2.1-2.1" />
                              </g>
                              {/* Small gear - rotates counter-clockwise */}
                              <g style={{ transformOrigin: '36px 14px', animation: 'spin 2s linear infinite reverse' }}>
                                <circle cx="36" cy="14" r="7" stroke="#3b82f6" strokeWidth={1.5} opacity={0.3} />
                                <circle cx="36" cy="14" r="3" stroke="#3b82f6" strokeWidth={1.2} />
                                <path strokeLinecap="round" strokeWidth={1.5} stroke="#3b82f6" d="M36 5v2.5M36 20.5v2.5M27 14h2.5M43.5 14h2.5" />
                              </g>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Step 2: Proposal Generation */}
                {(() => {
                  const isGenerating = generationStatus && ['queued', 'workers_running', 'generating_proposal', 'rating_proposal', 'rating_below_threshold'].includes(generationStatus);
                  const isFailed = generationStatus === 'failed';
                  const isClickable = proposals.length > 0 || isGenerating || isFailed;
                  const isProposalsSelected = selectedPipelineStep === 'proposals';

                  return (
                    <div
                      className={`p-4 rounded-lg border transition-shadow ${
                        isFailed
                          ? `border-red-200 bg-red-50 cursor-pointer hover:shadow-md ${isProposalsSelected ? 'ring-2 ring-red-500 shadow-md' : ''}`
                          : isGenerating
                          ? `border-blue-200 bg-blue-50 cursor-pointer hover:shadow-md ${isProposalsSelected ? 'ring-2 ring-blue-500 shadow-md' : ''}`
                          : proposals.length > 0
                            ? `border-green-200 bg-green-50 cursor-pointer hover:shadow-md ${isProposalsSelected ? 'ring-2 ring-green-500 shadow-md' : ''}`
                            : 'border-gray-200 bg-gray-50'
                      }`}
                      onClick={isClickable ? () => setSelectedPipelineStep(prev => prev === 'proposals' ? null : 'proposals') : undefined}
                    >
                      <div className={`flex ${isGenerating || isFailed ? 'gap-4' : ''}`}>
                        {/* Left column - content */}
                        <div className={`flex flex-col ${isGenerating || isFailed ? 'flex-[4]' : 'flex-1'}`}>
                          <div className="flex items-center gap-3">
                            {isFailed ? (
                              <div className="w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center bg-red-600 text-white text-sm font-bold">!</div>
                            ) : isGenerating ? (
                              <div className="relative w-8 h-8 flex-shrink-0 flex items-center justify-center">
                                <div className="absolute inset-0 rounded-full border-2 border-blue-200 border-t-blue-600 animate-spin" />
                                <span className="text-xs font-bold text-blue-600">2</span>
                              </div>
                            ) : (
                              <div className={`w-8 h-8 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${
                                proposals.length > 0 ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-600'
                              }`}>2</div>
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-gray-900">Proposal Generation</h4>
                                {isFailed && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-red-600 text-white rounded-full">
                                    Failed
                                  </span>
                                )}
                                {isGenerating && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-blue-600 text-white rounded-full">
                                    <span className="w-1 h-1 bg-white rounded-full animate-pulse" />
                                    Generating
                                  </span>
                                )}
                              </div>
                              {isFailed && (
                                <p className="text-xs text-red-600 mt-0.5">An error occurred during generation</p>
                              )}
                              {isGenerating && (
                                <p className="text-xs text-gray-500 mt-0.5">4 intelligence workers analyzing your deal</p>
                              )}
                            </div>
                          </div>
                          <p className={`text-sm mt-3 flex-1 ${isFailed ? 'text-red-700' : isGenerating ? 'text-blue-700' : 'text-gray-500'}`}>
                            {isFailed
                              ? 'Proposal generation failed. Click to retry.'
                              : isGenerating
                              ? `${generationWorkersCompleted || 0}/4 workers completed`
                              : proposals.length > 0
                                ? `${proposals.length} proposal(s) generated`
                                : (decisionEngine?.human_decision === 'proceed' || externalResearch?.human_decision === 'proceed')
                                  ? 'Generating proposal...'
                                  : 'Waiting for human review approval'}
                          </p>
                          {isGenerating && (
                            <div className="mt-3">
                              <div className="w-full bg-blue-100 rounded-full h-1.5 overflow-hidden">
                                <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: `${generationStatus === 'workers_running' ? Math.round(10 + (generationWorkersCompleted / 4) * 45) : generationStatus === 'generating_proposal' ? 65 : generationStatus === 'rating_proposal' ? 85 : 5}%`, transition: 'width 0.5s ease' }} />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Right column - gear animation */}
                        {isGenerating && (
                          <div className="flex-1 flex items-center justify-center">
                            <svg viewBox="0 0 48 48" fill="none" className="w-full h-full max-w-[80px] max-h-[80px]">
                              <g style={{ transformOrigin: '18px 28px', animation: 'spin 3s linear infinite' }}>
                                <circle cx="18" cy="28" r="10" stroke="#2563eb" strokeWidth={1.8} opacity={0.3} />
                                <circle cx="18" cy="28" r="4" stroke="#2563eb" strokeWidth={1.5} />
                                <path strokeLinecap="round" strokeWidth={2} stroke="#2563eb" d="M18 16v3M18 37v3M6 28h3M27 28h3" />
                                <path strokeLinecap="round" strokeWidth={2} stroke="#2563eb" d="M9.5 19.5l2.1 2.1M24.4 34.4l2.1 2.1M9.5 36.5l2.1-2.1M24.4 21.6l2.1-2.1" />
                              </g>
                              <g style={{ transformOrigin: '36px 14px', animation: 'spin 2s linear infinite reverse' }}>
                                <circle cx="36" cy="14" r="7" stroke="#3b82f6" strokeWidth={1.5} opacity={0.3} />
                                <circle cx="36" cy="14" r="3" stroke="#3b82f6" strokeWidth={1.2} />
                                <path strokeLinecap="round" strokeWidth={1.5} stroke="#3b82f6" d="M36 5v2.5M36 20.5v2.5M27 14h2.5M43.5 14h2.5" />
                              </g>
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Merged Analysis & Human Review Details */}
                {/* Analysis Failed - Retry Panel */}
                {selectedPipelineStep === 'analysis' && (decisionEngine?.status === 'failed' || externalResearch?.status === 'failed') && (
                  <div className="md:col-span-2">
                    <div className="bg-white rounded-xl border border-red-200 shadow-sm">
                      <div className="p-8 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Analysis Failed</h3>
                        <p className="text-sm text-gray-500 mb-2">
                          {decisionEngine?.status === 'failed' && externalResearch?.status === 'failed'
                            ? 'Both Decision Engine and External Research failed.'
                            : decisionEngine?.status === 'failed'
                              ? 'Decision Engine analysis failed.'
                              : 'External Research failed.'}
                        </p>
                        <p className="text-sm text-gray-400 mb-4">You can retry the analysis to regenerate the results.</p>
                        {canManageProposals && (
                        <button
                          onClick={async () => {
                            try {
                              await triggerDecisionEngine(leadId);
                              toast.success('Analysis re-triggered');
                              fetchProposalData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || 'Failed to retry analysis');
                            }
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                          Retry Analysis
                        </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedPipelineStep === 'analysis' && decisionEngine?.status === 'completed' && (
                  <div className="md:col-span-2 bg-white rounded-lg border border-gray-200 p-5 space-y-6">
                    {/* Decision Engine Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                          <svg className="w-3.5 h-3.5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-gray-900">Decision Engine Analysis</h3>
                      </div>

                      {decisionEngine.overall_assessment && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Executive Summary</h4>
                          <p className="text-gray-700 leading-relaxed">{decisionEngine.overall_assessment}</p>
                        </div>
                      )}

                      {decisionEngine.key_findings?.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Key Findings</h4>
                          <div className="space-y-2">
                            {decisionEngine.key_findings.map((finding, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <span className={`mt-1 flex-shrink-0 w-2 h-2 rounded-full ${
                                  finding.impact === 'positive' ? 'bg-green-500' :
                                  finding.impact === 'negative' ? 'bg-red-500' : 'bg-gray-400'
                                }`} />
                                <div>
                                  <span className="text-sm font-medium text-gray-800">{finding.finding}</span>
                                  {finding.detail && <p className="text-sm text-gray-500">{finding.detail}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {decisionEngine.checks?.length > 0 && (
                        <div className="mb-4">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Validation Checks</h4>
                          {decisionEngine.checks.map((check, idx) => (
                            <DecisionEngineCard key={idx} check={check} />
                          ))}
                        </div>
                      )}

                      {decisionEngine.risk_flags?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Risk Flags</h4>
                          <div className="space-y-2">
                            {decisionEngine.risk_flags.map((flag, idx) => (
                              <div key={idx} className={`rounded-lg p-3 border ${
                                flag.severity === 'high' ? 'bg-red-50 border-red-200' :
                                flag.severity === 'medium' ? 'bg-yellow-50 border-yellow-200' :
                                'bg-blue-50 border-blue-200'
                              }`}>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs font-semibold uppercase ${
                                    flag.severity === 'high' ? 'text-red-600' :
                                    flag.severity === 'medium' ? 'text-yellow-600' : 'text-blue-600'
                                  }`}>{flag.severity}</span>
                                  <span className="text-sm font-medium text-gray-800">{flag.flag}</span>
                                </div>
                                {flag.detail && <p className="text-sm text-gray-600">{flag.detail}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* External Research Section */}
                    {externalResearch?.status === 'completed' && (
                      <div className="pt-5 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-7 h-7 rounded-full bg-cyan-100 flex items-center justify-center">
                            <svg className="w-3.5 h-3.5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-bold text-gray-900">External Market Intelligence</h3>
                        </div>

                        {externalResearch.summary && (
                          <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Research Summary</h4>
                            <p className="text-gray-700 leading-relaxed">{externalResearch.summary}</p>
                          </div>
                        )}

                        {externalResearch.research_results?.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Research Findings</h4>
                            {externalResearch.research_results.map((item, idx) => (
                              <ExternalResearchCard key={idx} research={item} />
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Human Review: Worker Selection + Actions */}
                    {externalResearch?.status === 'completed' && !(decisionEngine?.human_decision || externalResearch?.human_decision) && canManageProposals && (
                      <div className="pt-5 border-t border-gray-200 space-y-5">
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
                              <svg className="w-3.5 h-3.5 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Review & Generate Proposal</h3>
                          </div>
                          <p className="text-sm text-gray-500 mb-4">
                            All workers will run. Checked workers produce required sections; unchecked produce supplementary analysis.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {WORKER_LIST.map(worker => (
                              <label key={worker.type} className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition">
                                <input
                                  type="checkbox"
                                  checked={workerModes[worker.type]}
                                  onChange={() => setWorkerModes(prev => ({
                                    ...prev,
                                    [worker.type]: !prev[worker.type],
                                  }))}
                                  className="w-4 h-4 flex-shrink-0 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                />
                                <div className="min-w-0">
                                  <span className="block text-sm font-medium text-gray-700">{worker.label}</span>
                                  <span className={`block text-xs ${workerModes[worker.type] ? 'text-primary-500' : 'text-gray-400'}`}>
                                    ({workerModes[worker.type] ? 'Required section' : 'Supplementary'})
                                  </span>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        {canManageProposals && (
                        <div className="flex flex-col gap-4 pt-4 border-t border-gray-200">
                          {!showDecline && !showFollowup ? (
                            <div className="flex flex-col sm:flex-row gap-3">
                              <button
                                onClick={handleReviewProceed}
                                disabled={reviewActionLoading}
                                className="flex-1 py-2.5 bg-primary text-white rounded-lg font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors"
                              >
                                {reviewActionLoading ? 'Starting Proposal Generation...' : 'Generate Proposal'}
                              </button>
                              <button
                                onClick={() => { setShowFollowup(true); setShowDecline(false); }}
                                className="flex-1 py-2.5 border border-primary text-primary rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors"
                              >
                                Schedule Follow-up
                              </button>
                              <button
                                onClick={() => { setShowDecline(true); setShowFollowup(false); }}
                                className="flex-1 py-2.5 border border-red-300 text-red-600 rounded-lg font-semibold hover:bg-red-600 hover:text-white transition-colors"
                              >
                                Decline
                              </button>
                            </div>
                          ) : showFollowup ? (
                            <div className="w-full space-y-3">
                              <textarea
                                value={followupComment}
                                onChange={(e) => setFollowupComment(e.target.value)}
                                placeholder="Follow-up comment (e.g., topics to discuss, required documents)..."
                                rows={2}
                                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                              />
                              <div className="flex gap-3">
                                <button
                                  onClick={handleReviewFollowup}
                                  disabled={reviewActionLoading}
                                  className="flex-1 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors"
                                >
                                  {reviewActionLoading ? 'Scheduling...' : 'Confirm & Schedule Meeting'}
                                </button>
                                <button
                                  onClick={() => setShowFollowup(false)}
                                  className="px-6 py-2.5 border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="w-full space-y-3">
                              <label className="block text-sm font-semibold text-gray-700">
                                Reason for declining <span className="text-gray-400 font-normal">(required)</span>
                              </label>
                              <textarea
                                value={declineComment}
                                onChange={(e) => setDeclineComment(e.target.value)}
                                placeholder="Explain why this lead is being declined (minimum 10 characters)..."
                                rows={3}
                                autoFocus
                                className="w-full rounded-lg border border-gray-300 px-3 py-2 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                              />
                              <p className="text-xs text-gray-400">
                                {declineComment.trim().length < 10
                                  ? `${10 - declineComment.trim().length} more character${10 - declineComment.trim().length === 1 ? '' : 's'} required`
                                  : `${declineComment.trim().length} characters`
                                }
                              </p>
                              <div className="flex gap-3">
                                <button
                                  onClick={handleReviewDecline}
                                  disabled={reviewActionLoading || declineComment.trim().length < 10}
                                  className="flex-1 py-2.5 border border-primary text-primary rounded-lg font-medium hover:bg-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {reviewActionLoading ? 'Declining...' : 'Confirm Decline'}
                                </button>
                                <button
                                  onClick={() => setShowDecline(false)}
                                  className="px-6 py-2.5 border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                        )}
                      </div>
                    )}

                    {/* Already Decided */}
                    {(decisionEngine?.human_decision || externalResearch?.human_decision) && (() => {
                      const decision = decisionEngine?.human_decision || externalResearch?.human_decision;
                      const comment = decisionEngine?.human_decision_comment || externalResearch?.human_decision_comment;
                      const isFollowup = decision === 'schedule_follow_up';
                      const isProceed = decision === 'proceed';
                      return (
                        <div className="pt-5 border-t border-gray-200">
                          <div className={`rounded-lg p-4 border ${
                            isProceed ? 'bg-primary-50 border-primary-200'
                              : isFollowup ? 'bg-accent-50 border-accent-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}>
                            <p className={`text-sm font-medium ${
                              isProceed ? 'text-primary-700' : isFollowup ? 'text-accent-700' : 'text-gray-700'
                            }`}>
                              Decision: {isProceed ? 'Approved - Proposal generation triggered' : isFollowup ? 'Schedule Follow-up' : 'Declined'}
                              {comment && <span className="font-normal"> - "{comment}"</span>}
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Proposal Generation Failed */}
                {selectedPipelineStep === 'proposals' && generationStatus === 'failed' && (
                  <div className="md:col-span-2">
                    <div className="bg-white rounded-xl border border-red-200 shadow-sm">
                      <div className="p-8 text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-4">
                          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Generation Failed</h3>
                        <p className="text-sm text-gray-500 mb-4">An error occurred during proposal generation. You can retry the process.</p>
                        {canManageProposals && (
                        <button
                          onClick={handleRetryProposalGeneration}
                          disabled={regenerateLoading}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          {regenerateLoading ? (
                            <>
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                              Retrying...
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                              Retry Generation
                            </>
                          )}
                        </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Proposal Generation Progress */}
                {selectedPipelineStep === 'proposals' && (
                  generationStatus && ['queued', 'workers_running', 'generating_proposal', 'rating_proposal', 'rating_below_threshold'].includes(generationStatus)
                ) && (
                  <div className="md:col-span-2 relative rounded-2xl bg-gradient-to-br from-primary-50 via-white to-accent-50 border border-primary-100 p-6 overflow-clip">
                    {/* Background logo watermarks - edges, partially outside, non-overlapping */}
                    {[
                      'top-[-60px] left-[-60px] w-[320px] h-[320px] opacity-[0.03]',
                      'top-[-80px] right-[-80px] w-[460px] h-[460px] opacity-[0.04]',
                      'bottom-[-80px] left-[-80px] w-[460px] h-[460px] opacity-[0.03]',
                      'bottom-[-60px] right-[-60px] w-[320px] h-[320px] opacity-[0.035]',
                    ].map((pos, i) => (
                      <img key={i} src="/images/logo-icon.svg" alt="" className={`absolute ${pos} pointer-events-none select-none`} />
                    ))}
                    <div className="relative z-10 w-full max-w-2xl mx-auto">
                      <ProposalGenerationProgress
                        status={generationStatus}
                        workersCompleted={generationWorkersCompleted ?? 0}
                        workerOutputs={generationWorkerOutputs}
                        startTime={generationStartTime}
                        onRetry={() => { setGenerationStatus(null); setSelectedPipelineStep('analysis'); }}
                        rating={generationRating}
                        ratingReasons={generationRatingReasons}
                      />
                    </div>
                  </div>
                )}

                {/* Proposal List */}
                {selectedPipelineStep === 'proposals' && (!generationStatus || generationStatus === 'completed' || !['queued', 'workers_running', 'generating_proposal', 'rating_proposal', 'rating_below_threshold'].includes(generationStatus)) && proposals.length > 0 && (
                  <div className="md:col-span-2">
                    <h4 className="font-medium text-gray-900 mb-3">Generated Proposals</h4>
                    <div className="space-y-3">
                      {proposals.map(proposal => (
                        <div key={proposal.id}
                          className={`p-4 rounded-lg border-2 transition cursor-pointer ${
                            proposal.is_active
                              ? 'bg-white border-primary-400 ring-2 ring-primary-100 shadow-sm'
                              : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => navigate(`/leads/${leadId}/proposals/${proposal.id}`)}>
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="min-w-0">
                                <div className="flex items-center flex-wrap gap-2">
                                  <h5 className={`font-medium ${proposal.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                                    {proposal.title || 'Untitled Proposal'}
                                  </h5>
                                  {proposal.is_active ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-primary-600 text-white rounded-full">
                                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
                                      Active Version
                                    </span>
                                  ) : (
                                    <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-500 rounded-full">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                                <p className={`text-sm mt-1 ${proposal.is_active ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Version {proposal.version} | Created {new Date(proposal.created_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center flex-wrap gap-2 sm:flex-shrink-0">
                              {canManageProposals && !proposal.is_active && (
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await activateProposal(leadId, proposal.id);
                                      toast.success(`Version ${proposal.version} set as active`);
                                      fetchProposalData();
                                    } catch (err) {
                                      toast.error('Failed to activate version');
                                    }
                                  }}
                                  className="px-2.5 py-1 text-xs font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
                                >
                                  Set as Active
                                </button>
                              )}
                              <ProposalStatusBadge status={proposal.status} />
                            </div>
                          </div>
                          {proposal.executive_summary && (
                            <p className={`text-sm mt-2 line-clamp-2 ${proposal.is_active ? 'text-gray-600' : 'text-gray-400'}`}>{proposal.executive_summary}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {!decisionEngine && questionnaire?.completion_status?.status !== 'completed' && (
                  <div className="md:col-span-2 text-center py-6 text-gray-500">
                    <p className="text-sm">Complete the questionnaire to start the proposal pipeline.</p>
                  </div>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Meeting Status Confirmation Modal */}
      <ConfirmationModal
        isOpen={showStatusModal}
        onClose={() => { setShowStatusModal(false); setPendingStatusUpdate(null); }}
        onConfirm={handleConfirmMeetingStatus}
        title="Update Meeting Status"
        message={pendingStatusUpdate ? `Change meeting status to "${pendingStatusUpdate.status}"?` : ''}
        confirmText="Update"
        cancelText="Cancel"
        confirmVariant="primary"
        isLoading={statusUpdateLoading}
      />

      {/* Telephonic Conversation Modal */}
      <TelephonicConversationModal
        isOpen={showTelephonicModal}
        leadId={leadId}
        leadName={lead?.name}
        onClose={() => setShowTelephonicModal(false)}
        onSuccess={() => {
          setShowTelephonicModal(false);
          toast.success('Conversation saved. Questionnaire is being updated.');
          fetchMeetings();
          fetchQuestionnaire();
        }}
      />

      {/* Manual Transcript Modal */}
      <Modal
        isOpen={!!showManualTranscriptModal}
        onClose={() => { setShowManualTranscriptModal(null); setManualTranscriptText(''); }}
        title="Add Transcript Manually"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => { setShowManualTranscriptModal(null); setManualTranscriptText(''); }} disabled={savingManualTranscript}>Cancel</Button>
            <Button onClick={handleSaveManualMeetingTranscript} disabled={savingManualTranscript}>{savingManualTranscript ? 'Saving...' : 'Save Transcript'}</Button>
          </>
        }
      >
        <div>
          <textarea
            value={manualTranscriptText}
            onChange={(e) => setManualTranscriptText(e.target.value)}
            placeholder="Paste the meeting transcript here..."
            className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary resize-none font-mono text-sm"
            disabled={savingManualTranscript}
          />
        </div>
      </Modal>

      {/* Meeting Notes Modal */}
      <Modal
        isOpen={!!showMeetingNotesModal}
        onClose={() => setShowMeetingNotesModal(null)}
        title="Meeting Notes"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowMeetingNotesModal(null)} disabled={savingMeetingNotes}>Cancel</Button>
            <Button onClick={handleSaveMeetingNotes} disabled={savingMeetingNotes}>{savingMeetingNotes ? 'Saving...' : 'Save Notes'}</Button>
          </>
        }
      >
        <div>
          <textarea
            value={meetingNotes}
            onChange={(e) => setMeetingNotes(e.target.value)}
            placeholder="Enter meeting notes..."
            className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary resize-none"
            disabled={savingMeetingNotes}
          />
        </div>
      </Modal>

      {/* View Transcript Modal */}
      <Modal
        isOpen={!!viewTranscriptModal}
        onClose={() => setViewTranscriptModal(null)}
        title={`Transcript — ${meetings.find(m => m.id === viewTranscriptModal)?.title || 'Meeting'}`}
        size="lg"
      >
        {viewTranscriptModal && meetingTranscripts[viewTranscriptModal] ? (
          <div className="space-y-4">
            {/* Meeting Notes */}
            {meetingTranscripts[viewTranscriptModal].meeting_notes && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-r-lg p-3 mb-4">
                <h6 className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase mb-1">Meeting Notes</h6>
                <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{meetingTranscripts[viewTranscriptModal].meeting_notes}</p>
              </div>
            )}
            {/* Tabbed Transcript View */}
            <TranscriptTabView transcriptData={meetingTranscripts[viewTranscriptModal]} />
          </div>
        ) : (
          <p className="text-sm text-gray-500 text-center py-4">No transcript data available.</p>
        )}
      </Modal>

      {/* Notes Modal */}
      <Modal
        isOpen={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        title="Notes"
      >
        <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
          {lead.notes}
        </div>
      </Modal>
      {/* Low Rating Confirmation Modal */}
      {showLowRatingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-6 py-5 bg-amber-50 border-b border-amber-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-xl font-bold text-amber-700">{generationRating || '?'}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-amber-900">AI Expert Analysis: {generationRating}/10</h3>
                  <p className="text-sm text-amber-700">Expert analysis rated this deal below the quality threshold</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5 max-h-80 overflow-y-auto">
              {generationRatingReasons && generationRatingReasons.length > 0 ? (
                <div className="space-y-2">
                  {generationRatingReasons.map((reason, idx) => (
                    <div key={idx} className={`rounded-lg border px-3 py-2.5 flex items-start gap-2.5 ${
                      reason.impact === 'negative' ? 'bg-red-50 border-red-200'
                        : reason.impact === 'positive' ? 'bg-green-50 border-green-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div>
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                          reason.impact === 'negative' ? 'text-red-600' :
                          reason.impact === 'positive' ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          {(reason.category || '').replace(/_/g, ' ')}
                        </span>
                        <p className="text-xs text-gray-700 leading-relaxed">{reason.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No detailed reasons available.</p>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-3">
              <button
                onClick={() => { setShowLowRatingModal(false); setGenerationStatus(null); }}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleForceGenerate}
                disabled={forceGenerating}
                className="px-5 py-2.5 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
              >
                {forceGenerating ? 'Generating...' : 'Force Generate Anyway'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetails;

